import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import {
    billingStatusToneClass,
    formatBillingAuditActionLabel,
    formatBillingAuditTargetLabel,
    formatBillingModeLabel,
    formatBillingStatusLabel,
    formatPromotionRewardTargetRoleLabel,
    formatPromotionRewardTypeLabel,
    formatRefundActionLabel,
} from '../lib/billingLabels'
import { formatDate, formatDateTimeMinute } from '../lib/date'
import { ENABLE_BILLING_COUPONS, ENABLE_BILLING_POINTS } from '../config/featureFlags'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'

/** 감사 로그 payload에서 구독 DTO(또는 quota 응답 내 subscription)를 꺼냅니다. */
function pickSubscriptionSnapshot(obj) {
    if (!obj || typeof obj !== 'object') return null
    if (obj.subscription && typeof obj.subscription === 'object') return obj.subscription
    if (obj.targetName != null || obj.planCode != null) return obj
    return null
}

function formatAuditFieldValue(key, val) {
    if (val == null || val === '') return '-'
    if (typeof val === 'object') {
        if (key === 'activeTrialGrant') {
            const st = val.status
            const days = val.days ?? val.daysGranted
            if (st != null) return days != null ? `${st} (${days}일)` : String(st)
            try {
                return JSON.stringify(val)
            } catch {
                return String(val)
            }
        }
        try {
            return JSON.stringify(val)
        } catch {
            return String(val)
        }
    }
    return String(val)
}

function pickAuditStatus(log) {
    return log?.afterState?.status || log?.beforeState?.status || log?.metadata?.status || ''
}

/** 구독일 부여 프리셋(일) — 복수 선택 시 합산 */
const GRANT_DAY_PRESET_OPTIONS = [7, 14, 30, 60, 90]

const SUBSCRIPTION_PAGE_SIZES = [10, 20, 50]

/** 운영자 과금/구독 허브(탭) 베이스 — App 라우트와 동일하게 유지 */
const BILLING_SUBSCRIPTIONS_HUB_BASE = '/subscriptions/hub'

function billingAdminHubPath(segment) {
    return `${BILLING_SUBSCRIPTIONS_HUB_BASE}/${segment}`
}

function resolveBillingAdminMenuId(pathname) {
    if (pathname === '/subscriptions/dashboard') {
        return 'dashboard'
    }
    const prefix = `${BILLING_SUBSCRIPTIONS_HUB_BASE}/`
    if (pathname.startsWith(prefix)) {
        const seg = pathname.slice(prefix.length)
        const segToId = {
            status: 'status',
            orders: 'orders',
            refunds: 'refunds',
            'billing-logs': 'billingLogs',
            'audit-logs': 'audit',
            coupons: 'coupons',
            points: 'points',
        }
        return segToId[seg] || 'status'
    }
    return 'status'
}

/** 목록 API만 별도 로딩·에러로 처리하는 과금 관리 탭 */
const BILLING_ADMIN_LIST_TAB_IDS = new Set(['status', 'orders', 'refunds', 'audit', 'billingLogs'])

function billingAdminListTabActive(menuId) {
    return BILLING_ADMIN_LIST_TAB_IDS.has(menuId)
}

/** axios 응답에 있으면 괄호로 덧붙여 표시 */
function pickBillingLoadDetailSuffix(e) {
    const d = e?.response?.data
    const m = (typeof d?.message === 'string' && d.message.trim())
        || (typeof d?.error === 'string' && d.error.trim())
        || ''
    if (m) return ` (${m})`
    const em = typeof e?.message === 'string' ? e.message.trim() : ''
    if (em && !/^Request failed with status code \d+$/i.test(em) && em !== 'Network Error') {
        return ` (${em})`
    }
    return ''
}

const AUDIT_SUBSCRIPTION_COMPARE_ROWS = [
    { key: 'targetType', label: '대상 유형' },
    { key: 'targetName', label: '대상 이름' },
    { key: 'orgName', label: '조직' },
    { key: 'planName', label: '플랜' },
    { key: 'planCode', label: '플랜 코드' },
    { key: 'status', label: '구독 상태', format: (v) => formatBillingStatusLabel(v, 'subscription') },
    { key: 'billingMode', label: '과금 모드', format: (v) => formatBillingModeLabel(v) },
    { key: 'startedAt', label: '시작일' },
    { key: 'endsAt', label: '종료일' },
    { key: 'nextResetAt', label: '다음 리셋' },
    { key: 'activeTrialGrant', label: 'Trial' },
]

function BillingAuditLogDetailModal({ log, onClose }) {
    if (!log) return null
    const before = log.beforeState
    const after = log.afterState
    const subBefore = pickSubscriptionSnapshot(before)
    const subAfter = pickSubscriptionSnapshot(after)
    const hasSubCompare = Boolean(subBefore || subAfter)
    const targetLine = [
        formatBillingAuditTargetLabel(log.targetType),
        log.targetId != null && log.targetId !== '' ? `#${log.targetId}` : '',
    ].filter(Boolean).join(' ')

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
            <div
                className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
                    <div>
                        <h3 className="text-lg font-semibold">이력 상세</h3>
                        <p className="mt-1 text-sm text-slate-500">{formatBillingAuditActionLabel(log.action)}</p>
                    </div>
                    <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={onClose}>
                        닫기
                    </button>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                    <InfoBlock label="시각" value={formatDateTimeMinute(log.createdAt)} />
                    <InfoBlock label="사유" value={log.reason || '-'} />
                    <div className="rounded border p-4 md:col-span-2">
                        <div className="text-slate-500">행위자</div>
                        <div className="mt-2 font-medium">{log.actorName || '-'}</div>
                        <div className="mt-1 text-xs text-slate-500 break-all">{log.actorEmail || '-'}</div>
                    </div>
                    <div className="rounded border p-4 md:col-span-2">
                        <div className="text-slate-500">대상</div>
                        <div className="mt-2 font-medium">{log.targetDisplayName || '-'}</div>
                        <div className="mt-1 text-xs text-slate-500">{targetLine || '-'}</div>
                    </div>
                </div>

                {hasSubCompare && (
                    <div className="mt-6">
                        <div className="text-sm font-semibold text-slate-800">구독 상태 (이전 → 이후)</div>
                        <p className="mt-1 text-xs text-slate-500">감사 시점에 저장된 스냅샷이며, 현재 DB 상태와 다를 수 있습니다.</p>
                        <div className="mt-3 overflow-x-auto rounded border">
                            <table className="min-w-full text-xs md:text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-3 py-2 text-left">항목</th>
                                        <th className="px-3 py-2 text-left">이전</th>
                                        <th className="px-3 py-2 text-left">이후</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const rows = AUDIT_SUBSCRIPTION_COMPARE_ROWS
                                            .map((row) => {
                                                const rawB = subBefore ? subBefore[row.key] : undefined
                                                const rawA = subAfter ? subAfter[row.key] : undefined
                                                const cellB = row.format ? row.format(rawB) : formatAuditFieldValue(row.key, rawB)
                                                const cellA = row.format ? row.format(rawA) : formatAuditFieldValue(row.key, rawA)
                                                return { row, cellB, cellA }
                                            })
                                            .filter(({ cellB, cellA }) => !(cellB === '-' && cellA === '-'))
                                        if (rows.length === 0) {
                                            return (
                                                <tr>
                                                    <td className="border-t px-3 py-4 text-center text-slate-500" colSpan={3}>
                                                        비교할 구독 필드가 없습니다. 아래 JSON을 참고하세요.
                                                    </td>
                                                </tr>
                                            )
                                        }
                                        return rows.map(({ row, cellB, cellA }) => (
                                            <tr key={row.key} className="border-t">
                                                <td className="px-3 py-2 text-slate-600">{row.label}</td>
                                                <td className="px-3 py-2 break-all text-slate-800">{cellB}</td>
                                                <td className="px-3 py-2 break-all text-slate-900">{cellA}</td>
                                            </tr>
                                        ))
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div className="mt-6">
                    <div className="text-sm font-semibold text-slate-800">저장된 변경 데이터</div>
                    <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded border bg-slate-50/80 p-3">
                            <div className="text-xs font-medium text-slate-600">이전 (before)</div>
                            <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-all text-xs text-slate-800">
                                {before == null ? '—' : JSON.stringify(before, null, 2)}
                            </pre>
                        </div>
                        <div className="rounded border bg-slate-50/80 p-3">
                            <div className="text-xs font-medium text-slate-600">이후 (after)</div>
                            <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-all text-xs text-slate-800">
                                {after == null ? '—' : JSON.stringify(after, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function SubscriptionsPage() {
    const { show } = useToast()
    const location = useLocation()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [subscriptionListLoading, setSubscriptionListLoading] = useState(false)
    const [subscriptionLoadError, setSubscriptionLoadError] = useState('')
    const [orderListLoading, setOrderListLoading] = useState(false)
    const [orderLoadError, setOrderLoadError] = useState('')
    const [refundListLoading, setRefundListLoading] = useState(false)
    const [refundLoadError, setRefundLoadError] = useState('')
    const [auditListLoading, setAuditListLoading] = useState(false)
    const [auditLoadError, setAuditLoadError] = useState('')
    const [error, setError] = useState('')
    const [dashboard, setDashboard] = useState(null)
    const [subscriptions, setSubscriptions] = useState([])
    const [subscriptionTotalCount, setSubscriptionTotalCount] = useState(0)
    const [subscriptionTotalPages, setSubscriptionTotalPages] = useState(1)
    const [subscriptionOrgOptions, setSubscriptionOrgOptions] = useState([])
    const [plans, setPlans] = useState([])
    const [orders, setOrders] = useState([])
    const [orderTotalCount, setOrderTotalCount] = useState(0)
    const [orderTotalPages, setOrderTotalPages] = useState(1)
    const [refunds, setRefunds] = useState([])
    const [refundTotalCount, setRefundTotalCount] = useState(0)
    const [refundTotalPages, setRefundTotalPages] = useState(1)
    const [auditLogs, setAuditLogs] = useState([])
    const [auditTotalCount, setAuditTotalCount] = useState(0)
    const [auditTotalPages, setAuditTotalPages] = useState(1)
    const [auditActionOptions, setAuditActionOptions] = useState([])
    const [auditTargetTypeOptions, setAuditTargetTypeOptions] = useState([])
    const [auditProviderOptions, setAuditProviderOptions] = useState([])
    const [auditStatusOptions, setAuditStatusOptions] = useState([])
    const [selectedAuditLog, setSelectedAuditLog] = useState(null)
    const [promotions, setPromotions] = useState([])
    const [pointOverview, setPointOverview] = useState(null)
    /** 구독일 부여 모달 대상(단일·복수) */
    const [grantTargets, setGrantTargets] = useState(null)
    const [grantDayPresetSelection, setGrantDayPresetSelection] = useState(() => new Set([7]))
    const [grantDaysExtra, setGrantDaysExtra] = useState('')
    const [selectedQuotaSubscription, setSelectedQuotaSubscription] = useState(null)
    const [quotaDetailLoading, setQuotaDetailLoading] = useState(false)
    const [quotaLimitDraft, setQuotaLimitDraft] = useState({})
    const [quotaLimitAdjustLoading, setQuotaLimitAdjustLoading] = useState(false)
    const [selectedTrialHistory, setSelectedTrialHistory] = useState(null)
    const [trialHistoryLoading, setTrialHistoryLoading] = useState(false)
    const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState([])
    const [subscriptionSearch, setSubscriptionSearch] = useState('')
    const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState('ALL')
    const [subscriptionOrgFilter, setSubscriptionOrgFilter] = useState('ALL')
    const [expiryFilter, setExpiryFilter] = useState('ALL')
    const [appliedSubscriptionSearch, setAppliedSubscriptionSearch] = useState('')
    const [appliedSubscriptionStatusFilter, setAppliedSubscriptionStatusFilter] = useState('ALL')
    const [appliedSubscriptionOrgFilter, setAppliedSubscriptionOrgFilter] = useState('ALL')
    const [appliedExpiryFilter, setAppliedExpiryFilter] = useState('ALL')
    const [subscriptionSortKey, setSubscriptionSortKey] = useState('endsAt')
    const [subscriptionSortDirection, setSubscriptionSortDirection] = useState('asc')
    const [subscriptionPageSize, setSubscriptionPageSize] = useState(10)
    const [subscriptionPage, setSubscriptionPage] = useState(1)
    const [orderSearch, setOrderSearch] = useState('')
    const [orderStatusFilter, setOrderStatusFilter] = useState('ALL')
    const [orderDateFrom, setOrderDateFrom] = useState('')
    const [orderDateTo, setOrderDateTo] = useState('')
    const [appliedOrderSearch, setAppliedOrderSearch] = useState('')
    const [appliedOrderStatusFilter, setAppliedOrderStatusFilter] = useState('ALL')
    const [appliedOrderDateFrom, setAppliedOrderDateFrom] = useState('')
    const [appliedOrderDateTo, setAppliedOrderDateTo] = useState('')
    const [orderSortKey, setOrderSortKey] = useState('paidAt')
    const [orderSortDirection, setOrderSortDirection] = useState('desc')
    const [orderPageSize, setOrderPageSize] = useState(10)
    const [orderPage, setOrderPage] = useState(1)
    const [refundSearch, setRefundSearch] = useState('')
    const [refundStatusFilter, setRefundStatusFilter] = useState('ALL')
    const [refundDateFrom, setRefundDateFrom] = useState('')
    const [refundDateTo, setRefundDateTo] = useState('')
    const [appliedRefundSearch, setAppliedRefundSearch] = useState('')
    const [appliedRefundStatusFilter, setAppliedRefundStatusFilter] = useState('ALL')
    const [appliedRefundDateFrom, setAppliedRefundDateFrom] = useState('')
    const [appliedRefundDateTo, setAppliedRefundDateTo] = useState('')
    const [refundSortKey, setRefundSortKey] = useState('createdAt')
    const [refundSortDirection, setRefundSortDirection] = useState('desc')
    const [refundPageSize, setRefundPageSize] = useState(10)
    const [refundPage, setRefundPage] = useState(1)
    const [auditSearch, setAuditSearch] = useState('')
    const [auditActionFilter, setAuditActionFilter] = useState('ALL')
    const [auditTargetTypeFilter, setAuditTargetTypeFilter] = useState('ALL')
    const [auditProviderFilter, setAuditProviderFilter] = useState('ALL')
    const [auditStatusFilter, setAuditStatusFilter] = useState('ALL')
    const [auditOrderNoFilter, setAuditOrderNoFilter] = useState('')
    const [auditPaymentIdFilter, setAuditPaymentIdFilter] = useState('')
    const [auditWebhookEventIdFilter, setAuditWebhookEventIdFilter] = useState('')
    const [auditDateFrom, setAuditDateFrom] = useState('')
    const [auditDateTo, setAuditDateTo] = useState('')
    const [appliedAuditSearch, setAppliedAuditSearch] = useState('')
    const [appliedAuditActionFilter, setAppliedAuditActionFilter] = useState('ALL')
    const [appliedAuditTargetTypeFilter, setAppliedAuditTargetTypeFilter] = useState('ALL')
    const [appliedAuditProviderFilter, setAppliedAuditProviderFilter] = useState('ALL')
    const [appliedAuditStatusFilter, setAppliedAuditStatusFilter] = useState('ALL')
    const [appliedAuditOrderNoFilter, setAppliedAuditOrderNoFilter] = useState('')
    const [appliedAuditPaymentIdFilter, setAppliedAuditPaymentIdFilter] = useState('')
    const [appliedAuditWebhookEventIdFilter, setAppliedAuditWebhookEventIdFilter] = useState('')
    const [appliedAuditDateFrom, setAppliedAuditDateFrom] = useState('')
    const [appliedAuditDateTo, setAppliedAuditDateTo] = useState('')
    const [auditSortKey, setAuditSortKey] = useState('createdAt')
    const [auditSortDirection, setAuditSortDirection] = useState('desc')
    const [auditPageSize, setAuditPageSize] = useState(10)
    const [auditPage, setAuditPage] = useState(1)
    const [bulkTrialModal, setBulkTrialModal] = useState(null)
    const [bulkTrialDays, setBulkTrialDays] = useState(7)
    const [bulkTrialReason, setBulkTrialReason] = useState('')
    const [bulkTrialLoading, setBulkTrialLoading] = useState(false)
    const [bulkStatus, setBulkStatus] = useState('')
    const [bulkPlanCode, setBulkPlanCode] = useState('')
    const [bulkActionLoading, setBulkActionLoading] = useState(false)
    const [bulkUpdateModal, setBulkUpdateModal] = useState(null)
    const [bulkUpdateReason, setBulkUpdateReason] = useState('')
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [orderDetailLoading, setOrderDetailLoading] = useState(false)
    const [grantReason, setGrantReason] = useState('')
    const [bulkComposeOpen, setBulkComposeOpen] = useState(false)
    const [bulkComposeStatus, setBulkComposeStatus] = useState('')
    const [bulkComposePlan, setBulkComposePlan] = useState('')
    const [bulkComposeGrantDays, setBulkComposeGrantDays] = useState('')
    const [bulkComposeQuotaFeature, setBulkComposeQuotaFeature] = useState('')
    const [bulkComposeQuotaDelta, setBulkComposeQuotaDelta] = useState('')
    const [bulkComposeReason, setBulkComposeReason] = useState('')
    const [bulkComposeLoading, setBulkComposeLoading] = useState(false)
    const [quotaConsumeLoading, setQuotaConsumeLoading] = useState(false)
    const [promotionSaving, setPromotionSaving] = useState(false)
    const [promotionForm, setPromotionForm] = useState(createInitialPromotionForm())
    const [editingPromotionId, setEditingPromotionId] = useState(null)
    const [billingBootstrapLoading, setBillingBootstrapLoading] = useState(false)
    const [billingBootstrapModal, setBillingBootstrapModal] = useState(null)
    const [billingBootstrapConfirmText, setBillingBootstrapConfirmText] = useState('')
    const [billingBootstrapResult, setBillingBootstrapResult] = useState(null)
    const [pointExpiryLoading, setPointExpiryLoading] = useState(false)
    const [couponReservationExpiryLoading, setCouponReservationExpiryLoading] = useState(false)

    const hubTabMenus = useMemo(() => {
        const tabs = [
            { id: 'status', name: '구독 상태', path: billingAdminHubPath('status') },
            { id: 'orders', name: '주문/결제', path: billingAdminHubPath('orders') },
            { id: 'refunds', name: '환불/취소', path: billingAdminHubPath('refunds') },
        ]
        if (ENABLE_BILLING_COUPONS) {
            tabs.push({ id: 'coupons', name: '쿠폰/할인', path: billingAdminHubPath('coupons') })
        }
        if (ENABLE_BILLING_POINTS) {
            tabs.push({ id: 'points', name: '포인트', path: billingAdminHubPath('points') })
        }
        tabs.push(
            { id: 'billingLogs', name: '과금로그', path: billingAdminHubPath('billing-logs') },
            { id: 'audit', name: '이력관리', path: billingAdminHubPath('audit-logs') },
        )
        return tabs
    }, [])
    const activeMenu = useMemo(() => resolveBillingAdminMenuId(location.pathname), [location.pathname])
    const isDashboardView = activeMenu === 'dashboard'
    const subscriptionsManualAnchor = useMemo(() => {
        if (activeMenu === 'dashboard' || activeMenu === 'status' || activeMenu === 'orders') return 'step-01'
        if (activeMenu === 'refunds' || activeMenu === 'billingLogs' || activeMenu === 'audit' || activeMenu === 'coupons' || activeMenu === 'points') return 'step-03'
        return ''
    }, [activeMenu])
    const orgOptions = subscriptionOrgOptions
    const selectedSubscriptions = useMemo(() => (
        subscriptions.filter((item) => selectedSubscriptionIds.includes(item.id))
    ), [subscriptions, selectedSubscriptionIds])

    const singleSelectedSubscription = selectedSubscriptions.length === 1 ? selectedSubscriptions[0] : null
    const selectedPlanLabel = useMemo(() => (
        plans.find((plan) => plan.planCode === bulkPlanCode)?.name || bulkPlanCode || '-'
    ), [plans, bulkPlanCode])

    const pagedSubscriptions = subscriptions
    const pagedOrders = orders
    const pagedRefunds = refunds
    const pagedAuditLogs = auditLogs

    const pageSelectedCount = useMemo(() => (
        subscriptions.filter((item) => selectedSubscriptionIds.includes(item.id)).length
    ), [subscriptions, selectedSubscriptionIds])

    useEffect(() => {
        setSubscriptionPage(1)
    }, [subscriptionSortKey, subscriptionSortDirection, subscriptionPageSize])

    useEffect(() => {
        if (!SUBSCRIPTION_PAGE_SIZES.includes(subscriptionPageSize)) {
            setSubscriptionPageSize(10)
        }
    }, [subscriptionPageSize])

    useEffect(() => {
        setOrderPage(1)
    }, [orderSortKey, orderSortDirection, orderPageSize])

    useEffect(() => {
        if (orderPage > orderTotalPages) {
            setOrderPage(orderTotalPages)
        }
    }, [orderPage, orderTotalPages])

    useEffect(() => {
        setRefundPage(1)
    }, [refundSortKey, refundSortDirection, refundPageSize])

    useEffect(() => {
        if (refundPage > refundTotalPages) {
            setRefundPage(refundTotalPages)
        }
    }, [refundPage, refundTotalPages])

    useEffect(() => {
        setAuditPage(1)
    }, [auditSortKey, auditSortDirection, auditPageSize])

    useEffect(() => {
        if (auditPage > auditTotalPages) {
            setAuditPage(auditTotalPages)
        }
    }, [auditPage, auditTotalPages])

    const loadData = async () => {
        const menu = activeMenu
        const statusMenu = menu === 'status'
        const ordersMenu = menu === 'orders'
        const refundsMenu = menu === 'refunds'
        const auditMenu = menu === 'audit' || menu === 'billingLogs'
        const clearAllListTabErrors = () => {
            setSubscriptionLoadError('')
            setOrderLoadError('')
            setRefundLoadError('')
            setAuditLoadError('')
        }
        if (statusMenu) {
            clearAllListTabErrors()
            setSubscriptionListLoading(true)
            setError('')
        } else if (ordersMenu) {
            clearAllListTabErrors()
            setOrderListLoading(true)
            setError('')
        } else if (refundsMenu) {
            clearAllListTabErrors()
            setRefundListLoading(true)
            setError('')
        } else if (auditMenu) {
            clearAllListTabErrors()
            setAuditListLoading(true)
            setError('')
        } else {
            setLoading(true)
            setError('')
            clearAllListTabErrors()
        }
        try {
            if (menu === 'dashboard') {
                const dashboardRes = await api.get('/billing/admin/dashboard')
                setDashboard(dashboardRes?.data?.data ?? null)
            } else if (menu === 'status') {
                const plansPromise = plans.length > 0
                    ? Promise.resolve({ data: { data: plans } })
                    : api.get('/billing/admin/config/plans')
                const [subscriptionsRes, plansRes] = await Promise.all([
                    api.get('/billing/admin/subscriptions', {
                        params: {
                            page: subscriptionPage,
                            size: subscriptionPageSize,
                            search: appliedSubscriptionSearch,
                            status: appliedSubscriptionStatusFilter,
                            orgName: appliedSubscriptionOrgFilter,
                            expiry: appliedExpiryFilter,
                            sortKey: subscriptionSortKey,
                            sortDirection: subscriptionSortDirection,
                        },
                    }),
                    plansPromise,
                ])
                const subscriptionPayload = subscriptionsRes?.data?.data ?? {}
                const nextSubscriptions = Array.isArray(subscriptionPayload?.items) ? subscriptionPayload.items : []
                setSubscriptions(nextSubscriptions)
                setSubscriptionTotalCount(Number(subscriptionPayload?.totalCount) || 0)
                setSubscriptionTotalPages(Number(subscriptionPayload?.totalPages) || 1)
                setSubscriptionOrgOptions(Array.isArray(subscriptionPayload?.orgOptions) ? subscriptionPayload.orgOptions : [])
                if ((Number(subscriptionPayload?.page) || 1) !== subscriptionPage) {
                    setSubscriptionPage(Number(subscriptionPayload?.page) || 1)
                }
                setSelectedSubscriptionIds((prev) => prev.filter((id) => nextSubscriptions.some((item) => item.id === id && item.billingReady !== false)))
                const nextPlans = Array.isArray(plansRes?.data?.data) ? plansRes.data.data : []
                if (nextPlans.length > 0) {
                    setPlans(nextPlans)
                }
            } else if (menu === 'orders') {
                const ordersRes = await api.get('/billing/admin/orders', {
                    params: {
                        page: orderPage,
                        size: orderPageSize,
                        search: appliedOrderSearch,
                        status: appliedOrderStatusFilter,
                        dateFrom: appliedOrderDateFrom,
                        dateTo: appliedOrderDateTo,
                        sortKey: orderSortKey,
                        sortDirection: orderSortDirection,
                    },
                })
                const orderPayload = ordersRes?.data?.data ?? {}
                setOrders(Array.isArray(orderPayload?.items) ? orderPayload.items : [])
                setOrderTotalCount(Number(orderPayload?.totalCount) || 0)
                setOrderTotalPages(Number(orderPayload?.totalPages) || 1)
                if ((Number(orderPayload?.page) || 1) !== orderPage) {
                    setOrderPage(Number(orderPayload?.page) || 1)
                }
            } else if (menu === 'refunds') {
                const refundsRes = await api.get('/billing/admin/refunds', {
                    params: {
                        page: refundPage,
                        size: refundPageSize,
                        search: appliedRefundSearch,
                        status: appliedRefundStatusFilter,
                        dateFrom: appliedRefundDateFrom,
                        dateTo: appliedRefundDateTo,
                        sortKey: refundSortKey,
                        sortDirection: refundSortDirection,
                    },
                })
                const refundPayload = refundsRes?.data?.data ?? {}
                setRefunds(Array.isArray(refundPayload?.items) ? refundPayload.items : [])
                setRefundTotalCount(Number(refundPayload?.totalCount) || 0)
                setRefundTotalPages(Number(refundPayload?.totalPages) || 1)
                if ((Number(refundPayload?.page) || 1) !== refundPage) {
                    setRefundPage(Number(refundPayload?.page) || 1)
                }
            } else if (menu === 'audit' || menu === 'billingLogs') {
                const auditsRes = await api.get('/billing/admin/audit-logs', {
                    params: {
                        page: auditPage,
                        size: auditPageSize,
                        search: appliedAuditSearch,
                        action: appliedAuditActionFilter,
                        targetType: appliedAuditTargetTypeFilter,
                        provider: appliedAuditProviderFilter,
                        status: appliedAuditStatusFilter,
                        orderNo: appliedAuditOrderNoFilter,
                        paymentId: appliedAuditPaymentIdFilter,
                        webhookEventId: appliedAuditWebhookEventIdFilter,
                        dateFrom: appliedAuditDateFrom,
                        dateTo: appliedAuditDateTo,
                        sortKey: auditSortKey,
                        sortDirection: auditSortDirection,
                    },
                })
                const auditPayload = auditsRes?.data?.data ?? {}
                setAuditLogs(Array.isArray(auditPayload?.items) ? auditPayload.items : [])
                setAuditTotalCount(Number(auditPayload?.totalCount) || 0)
                setAuditTotalPages(Number(auditPayload?.totalPages) || 1)
                setAuditActionOptions(Array.isArray(auditPayload?.actionOptions) ? auditPayload.actionOptions : [])
                setAuditTargetTypeOptions(Array.isArray(auditPayload?.targetTypeOptions) ? auditPayload.targetTypeOptions : [])
                setAuditProviderOptions(Array.isArray(auditPayload?.providerOptions) ? auditPayload.providerOptions : [])
                setAuditStatusOptions(Array.isArray(auditPayload?.statusOptions) ? auditPayload.statusOptions : [])
                if ((Number(auditPayload?.page) || 1) !== auditPage) {
                    setAuditPage(Number(auditPayload?.page) || 1)
                }
            } else if (menu === 'coupons') {
                const promotionsRes = await api.get('/billing/admin/promotions').catch(() => ({ data: { data: [] } }))
                setPromotions(Array.isArray(promotionsRes?.data?.data) ? promotionsRes.data.data : [])
            } else if (menu === 'points') {
                const pointsRes = await api.get('/billing/admin/points/overview').catch(() => ({ data: { data: null } }))
                setPointOverview(pointsRes?.data?.data ?? null)
            }
        } catch (e) {
            if (statusMenu) {
                setSubscriptionLoadError(`구독 목록을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else if (ordersMenu) {
                setOrderLoadError(`주문 목록을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else if (refundsMenu) {
                setRefundLoadError(`환불 목록을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else if (auditMenu) {
                setAuditLoadError(`감사 이력을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else {
                setError(`과금/구독 데이터를 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            }
        } finally {
            if (statusMenu) {
                setSubscriptionListLoading(false)
            } else if (ordersMenu) {
                setOrderListLoading(false)
            } else if (refundsMenu) {
                setRefundListLoading(false)
            } else if (auditMenu) {
                setAuditListLoading(false)
            } else {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        loadData()
    }, [
        subscriptionPage, subscriptionPageSize, appliedSubscriptionSearch, appliedSubscriptionStatusFilter, appliedSubscriptionOrgFilter, appliedExpiryFilter, subscriptionSortKey, subscriptionSortDirection,
        orderPage, orderPageSize, appliedOrderSearch, appliedOrderStatusFilter, appliedOrderDateFrom, appliedOrderDateTo, orderSortKey, orderSortDirection,
        refundPage, refundPageSize, appliedRefundSearch, appliedRefundStatusFilter, appliedRefundDateFrom, appliedRefundDateTo, refundSortKey, refundSortDirection,
        auditPage, auditPageSize, appliedAuditSearch, appliedAuditActionFilter, appliedAuditTargetTypeFilter, appliedAuditProviderFilter, appliedAuditStatusFilter,
        appliedAuditOrderNoFilter, appliedAuditPaymentIdFilter, appliedAuditWebhookEventIdFilter, appliedAuditDateFrom, appliedAuditDateTo, auditSortKey, auditSortDirection,
        activeMenu,
    ])

    const toggleGrantDayPreset = (d) => {
        setGrantDayPresetSelection((prev) => {
            const next = new Set(prev)
            if (next.has(d)) next.delete(d)
            else next.add(d)
            return next
        })
    }

    const resolveGrantTotalDays = () => {
        let t = 0
        grantDayPresetSelection.forEach((n) => {
            t += Number(n) || 0
        })
        const ex = parseInt(String(grantDaysExtra || '').trim(), 10)
        if (!Number.isNaN(ex) && ex > 0) t += ex
        return t
    }

    const grantSubscriptionDays = async () => {
        if (!grantTargets?.items?.length) return
        const days = resolveGrantTotalDays()
        if (days < 1) {
            show('부여할 총 일수는 1일 이상이어야 합니다. 프리셋을 선택하거나 추가 일수를 입력해 주세요.', 'error')
            return
        }
        try {
            await Promise.all(grantTargets.items.map((it) => (
                api.post(`/billing/admin/subscriptions/${it.id}/grant-days`, {
                    days,
                    reason: grantReason || '운영자 수동 부여',
                })
            )))
            show(
                grantTargets.items.length > 1
                    ? `${grantTargets.items.length}명에게 구독일 ${days}일을 부여했습니다.`
                    : '구독일 부여가 완료되었습니다.',
                'success',
            )
            setGrantTargets(null)
            setGrantReason('')
            setGrantDayPresetSelection(new Set([7]))
            setGrantDaysExtra('')
            await loadData()
        } catch (e) {
            show('구독일 부여에 실패했습니다.', 'error')
        }
    }

    const toggleTrial = async (subscription, revoke = false) => {
        try {
            await api.post(`/billing/admin/subscriptions/${subscription.id}/trial`, {
                days: 7,
                revoke,
                reason: revoke ? 'Trial 회수' : '운영자 Trial 지급',
            })
            show(revoke ? 'Trial 회수가 완료되었습니다.' : 'Trial 지급이 완료되었습니다.', 'success')
            loadData()
        } catch (e) {
            show('Trial 처리에 실패했습니다.', 'error')
        }
    }

    const openSelectedGrantDays = () => {
        const ready = selectedSubscriptions.filter((s) => s.billingReady !== false)
        if (ready.length === 0) {
            show('구독이 준비된(billing) 회원을 1명 이상 선택해주세요.', 'error')
            return
        }
        setGrantTargets({ items: ready.map((s) => ({ id: s.id, targetName: s.targetName })) })
        setGrantDayPresetSelection(new Set([7]))
        setGrantDaysExtra('')
        setGrantReason('')
    }

    const openSelectedQuota = async () => {
        if (!singleSelectedSubscription) {
            show('할당량 상세는 1명의 회원을 선택한 뒤 확인해주세요.', 'error')
            return
        }
        try {
            setQuotaDetailLoading(true)
            const response = await api.get(`/billing/admin/subscriptions/${singleSelectedSubscription.id}`)
            setSelectedQuotaSubscription(response?.data?.data ?? null)
            setQuotaLimitDraft({})
        } catch (e) {
            show('할당량 상세를 불러오지 못했습니다.', 'error')
        } finally {
            setQuotaDetailLoading(false)
        }
    }

    const openSelectedTrialHistory = () => {
        if (!singleSelectedSubscription) {
            show('Trial 이력은 1명의 회원을 선택한 뒤 확인해주세요.', 'error')
            return
        }
        openTrialHistory(singleSelectedSubscription)
    }

    const updateSelectedSubscriptions = async (payload, successMessage, errorMessage, onSuccess) => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        try {
            setBulkActionLoading(true)
            await Promise.all(selectedSubscriptionIds.map((subscriptionId) => (
                api.patch(`/billing/admin/subscriptions/${subscriptionId}`, payload)
            )))
            show(successMessage, 'success')
            setSelectedSubscriptionIds([])
            if (typeof onSuccess === 'function') {
                onSuccess()
            }
            await loadData()
        } catch (e) {
            show(errorMessage, 'error')
        } finally {
            setBulkActionLoading(false)
        }
    }

    const openBulkStatusModal = () => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        if (!bulkStatus) {
            show('변경할 상태를 선택해주세요.', 'error')
            return
        }
        setBulkUpdateReason('운영자 일괄 상태 변경')
        setBulkUpdateModal({
            type: 'status',
            title: '선택 회원 상태 변경',
            nextValueLabel: formatBillingStatusLabel(bulkStatus, 'subscription'),
            payload: {
                status: bulkStatus,
                reason: '운영자 일괄 상태 변경',
            },
            successMessage: '선택한 회원의 구독 상태를 변경했습니다.',
            errorMessage: '구독 상태 변경에 실패했습니다.',
        })
    }

    const openBulkPlanModal = () => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        if (!bulkPlanCode) {
            show('변경할 플랜을 선택해주세요.', 'error')
            return
        }
        setBulkUpdateReason('운영자 일괄 플랜 변경')
        setBulkUpdateModal({
            type: 'plan',
            title: '선택 회원 플랜 변경',
            nextValueLabel: selectedPlanLabel,
            payload: {
                planCode: bulkPlanCode,
                reason: '운영자 일괄 플랜 변경',
            },
            successMessage: '선택한 회원의 플랜을 변경했습니다.',
            errorMessage: '플랜 변경에 실패했습니다.',
        })
    }

    const changeSubscriptionStatus = async (subscription, status) => {
        try {
            await api.patch(`/billing/admin/subscriptions/${subscription.id}`, {
                status,
                reason: '운영자 상태 변경',
            })
            show('구독 상태가 변경되었습니다.', 'success')
            loadData()
        } catch (e) {
            show('구독 상태 변경에 실패했습니다.', 'error')
        }
    }

    const reconcileOrder = async (orderId) => {
        try {
            await api.post(`/billing/admin/orders/${orderId}/reconcile`)
            show('주문 재검증 요청이 완료되었습니다.', 'success')
            loadData()
        } catch (e) {
            show('주문 재검증에 실패했습니다.', 'error')
        }
    }

    const updatePromotionForm = (field, value) => {
        setPromotionForm((prev) => ({ ...prev, [field]: value }))
    }

    const updatePromotionReward = (index, field, value) => {
        setPromotionForm((prev) => ({
            ...prev,
            rewards: prev.rewards.map((reward, rewardIndex) => (
                rewardIndex === index ? { ...reward, [field]: value } : reward
            )),
        }))
    }

    const addPromotionReward = () => {
        setPromotionForm((prev) => ({
            ...prev,
            rewards: [...prev.rewards, createInitialRewardForm()],
        }))
    }

    const removePromotionReward = (index) => {
        setPromotionForm((prev) => ({
            ...prev,
            rewards: prev.rewards.filter((_, rewardIndex) => rewardIndex !== index),
        }))
    }

    const loadPromotionToForm = (promotion) => {
        setEditingPromotionId(promotion.id)
        setPromotionForm({
            promotionType: promotion.promotionType || 'COUPON',
            triggerType: promotion.triggerType || 'CODE_REDEEM',
            code: promotion.code || '',
            name: promotion.name || '',
            description: promotion.description || '',
            status: promotion.status || 'ACTIVE',
            perUserLimit: promotion.perUserLimit || '',
            totalLimit: promotion.totalLimit || '',
            rewards: Array.isArray(promotion.rewards) && promotion.rewards.length > 0
                ? promotion.rewards.map((reward) => ({
                    rewardType: reward.rewardType || 'POINT_GRANT',
                    targetRole: reward.targetRole || 'REDEEMER',
                    value: reward.valueInt ?? reward.value ?? '',
                    expireDays: reward.valueJson?.expireDays ?? '',
                }))
                : [createInitialRewardForm()],
        })
    }

    const resetPromotionForm = () => {
        setEditingPromotionId(null)
        setPromotionForm(createInitialPromotionForm())
    }

    const submitPromotion = async () => {
        try {
            setPromotionSaving(true)
            const rewards = promotionForm.rewards
                .filter((reward) => reward.rewardType && String(reward.value || '').trim())
                .map((reward) => {
                    const payload = {
                        rewardType: reward.rewardType,
                        targetRole: reward.targetRole,
                        value: Number(reward.value) || 0,
                    }
                    if (reward.rewardType === 'POINT_GRANT' && reward.expireDays) {
                        payload.valueJson = { expireDays: Number(reward.expireDays) || 0 }
                    }
                    return payload
                })

            if (!promotionForm.name.trim()) {
                show('프로모션 이름을 입력해주세요.', 'error')
                return
            }
            if (rewards.length === 0) {
                show('최소 1개의 reward를 입력해주세요.', 'error')
                return
            }

            const payload = {
                promotionType: promotionForm.promotionType,
                triggerType: promotionForm.triggerType,
                code: promotionForm.code.trim() || undefined,
                name: promotionForm.name.trim(),
                description: promotionForm.description.trim() || undefined,
                status: promotionForm.status,
                perUserLimit: promotionForm.perUserLimit ? Number(promotionForm.perUserLimit) : undefined,
                totalLimit: promotionForm.totalLimit ? Number(promotionForm.totalLimit) : undefined,
                rewards,
            }

            if (editingPromotionId) {
                await api.patch(`/billing/admin/promotions/${editingPromotionId}`, payload)
                show('프로모션이 수정되었습니다.', 'success')
            } else {
                await api.post('/billing/admin/promotions', payload)
                show('프로모션이 생성되었습니다.', 'success')
            }
            resetPromotionForm()
            await loadData()
        } catch (e) {
            show(editingPromotionId ? '프로모션 수정에 실패했습니다.' : '프로모션 생성에 실패했습니다.', 'error')
        } finally {
            setPromotionSaving(false)
        }
    }

    const togglePromotionStatus = async (promotion, nextStatus) => {
        try {
            await api.patch(`/billing/admin/promotions/${promotion.id}`, {
                status: nextStatus,
                reason: `운영자 ${nextStatus === 'ACTIVE' ? '재활성화' : '비활성화'}`,
            })
            show(`프로모션을 ${nextStatus === 'ACTIVE' ? '재활성화' : '비활성화'}했습니다.`, 'success')
            await loadData()
        } catch (e) {
            show('프로모션 상태 변경에 실패했습니다.', 'error')
        }
    }

    const runPointExpiry = async () => {
        try {
            setPointExpiryLoading(true)
            const res = await api.post('/billing/admin/points/expire')
            const payload = res?.data?.data ?? null
            show(`포인트 만료 처리 완료: ${payload?.expiredAmount || 0}P / ${payload?.expiredLotCount || 0}건`, 'success')
            await loadData()
        } catch (e) {
            show('포인트 만료 처리에 실패했습니다.', 'error')
        } finally {
            setPointExpiryLoading(false)
        }
    }

    const runCouponReservationExpiry = async () => {
        try {
            setCouponReservationExpiryLoading(true)
            const res = await api.post('/billing/admin/coupons/reservations/expire')
            const payload = res?.data?.data ?? null
            show(`쿠폰 예약 만료 처리 완료: ${payload?.expiredCount || 0}건`, 'success')
            await loadData()
        } catch (e) {
            show('쿠폰 예약 만료 처리에 실패했습니다.', 'error')
        } finally {
            setCouponReservationExpiryLoading(false)
        }
    }

    const openOrderDetail = async (orderId) => {
        setOrderDetailLoading(true)
        try {
            const response = await api.get(`/billing/admin/orders/${orderId}`)
            setSelectedOrder(response?.data?.data ?? null)
        } catch (e) {
            show('주문 상세를 불러오지 못했습니다.', 'error')
        } finally {
            setOrderDetailLoading(false)
        }
    }

    const updateRefund = async (refundId, action) => {
        try {
            await api.post(`/billing/admin/refunds/${refundId}/${action}`)
            show(`환불 ${formatRefundActionLabel(action)} 처리가 완료되었습니다.`, 'success')
            loadData()
        } catch (e) {
            show('환불 처리에 실패했습니다.', 'error')
        }
    }

    const consumeSubscriptionQuota = async (subscriptionId, featureCode) => {
        try {
            setQuotaConsumeLoading(true)
            const response = await api.post(`/billing/admin/subscriptions/${subscriptionId}/usage/consume`, {
                featureCode,
                units: 1,
                refType: 'ADMIN_UI',
                reason: '운영자 수동 차감 테스트',
            })
            const updatedSubscription = response?.data?.data?.subscription
            if (updatedSubscription) {
                setSelectedQuotaSubscription(updatedSubscription)
            }
            show('사용량이 1건 차감되었습니다.', 'success')
            await loadData()
        } catch (e) {
            show('사용량 차감에 실패했습니다.', 'error')
        } finally {
            setQuotaConsumeLoading(false)
        }
    }

    const adjustSubscriptionQuotaLimitRow = async (subscriptionId, featureCode) => {
        const raw = String(quotaLimitDraft[featureCode] ?? '').trim()
        const delta = parseInt(raw, 10)
        if (Number.isNaN(delta) || delta === 0) {
            show('한도 증감은 0이 아닌 정수로 입력해주세요.', 'error')
            return
        }
        try {
            setQuotaLimitAdjustLoading(true)
            const response = await api.post(`/billing/admin/subscriptions/${subscriptionId}/quota-limit-adjust`, {
                featureCode,
                delta,
                reason: '운영자 한도 조정',
            })
            const updatedSubscription = response?.data?.data?.subscription
            if (updatedSubscription) {
                setSelectedQuotaSubscription(updatedSubscription)
            }
            setQuotaLimitDraft((prev) => {
                const next = { ...prev }
                delete next[featureCode]
                return next
            })
            show('할당량 한도가 반영되었습니다.', 'success')
            await loadData()
        } catch (e) {
            show(`한도 조정에 실패했습니다.${pickBillingLoadDetailSuffix(e)}`, 'error')
        } finally {
            setQuotaLimitAdjustLoading(false)
        }
    }

    const openTrialHistory = async (subscription) => {
        setTrialHistoryLoading(true)
        try {
            const response = await api.get(`/billing/admin/subscriptions/${subscription.id}/trial-grants`)
            setSelectedTrialHistory({
                subscription,
                rows: Array.isArray(response?.data?.data) ? response.data.data : [],
            })
        } catch (e) {
            show('Trial 이력을 불러오지 못했습니다.', 'error')
        } finally {
            setTrialHistoryLoading(false)
        }
    }

    const toggleSubscriptionSelection = (subscriptionId) => {
        const subscription = subscriptions.find((item) => item.id === subscriptionId)
        if (subscription?.billingReady === false) {
            return
        }
        setSelectedSubscriptionIds((prev) => (
            prev.includes(subscriptionId)
                ? prev.filter((id) => id !== subscriptionId)
                : [...prev, subscriptionId]
        ))
    }

    const toggleAllSubscriptionSelection = () => {
        const pageIds = pagedSubscriptions.filter((item) => item.billingReady !== false).map((item) => item.id)
        if (pageIds.length === 0) {
            return
        }
        const isAllSelected = pageIds.every((id) => selectedSubscriptionIds.includes(id))
        if (isAllSelected) {
            setSelectedSubscriptionIds((prev) => prev.filter((id) => !pageIds.includes(id)))
            return
        }
        setSelectedSubscriptionIds((prev) => Array.from(new Set([...prev, ...pageIds])))
    }

    const bootstrapMissingBilling = async ({ dryRun = false, confirmText = '', limit = 5000 } = {}) => {
        try {
            setBillingBootstrapLoading(true)
            const response = await api.post('/billing/admin/subscriptions/bootstrap-missing', { dryRun, confirmText, limit }, { __silent: true })
            const payload = response?.data?.data ?? null
            const summary = dryRun
                ? `누락 계정 ${payload?.missingAccounts || 0}건 / 누락 구독 ${payload?.missingSubscriptions || 0}건`
                : `계정 ${payload?.createdAccounts || 0}건 / 구독 ${payload?.createdSubscriptions || 0}건 생성`
            setBillingBootstrapResult({
                mode: dryRun ? 'dryRun' : 'execute',
                payload,
            })
            show(dryRun ? `dry-run 완료: ${summary}` : `bootstrap 완료: ${summary}`, 'success')
            await loadData()
        } catch (e) {
            show(e?.response?.data?.message || '누락 billing bootstrap 실행에 실패했습니다.', 'error')
        } finally {
            setBillingBootstrapLoading(false)
        }
    }

    const openBillingBootstrapModal = () => {
        setBillingBootstrapConfirmText('')
        setBillingBootstrapModal({ limit: 5000 })
    }

    const submitBillingBootstrap = async () => {
        if (!billingBootstrapModal) return
        const trimmed = billingBootstrapConfirmText.trim().toUpperCase()
        if (trimmed !== 'BOOTSTRAP') {
            show('확인 문구 BOOTSTRAP 을 입력해주세요.', 'error')
            return
        }
        await bootstrapMissingBilling({
            dryRun: false,
            confirmText: trimmed,
            limit: billingBootstrapModal.limit,
        })
        setBillingBootstrapModal(null)
        setBillingBootstrapConfirmText('')
    }

    const applySubscriptionFilters = () => {
        setSubscriptionPage(1)
        setAppliedSubscriptionSearch(subscriptionSearch)
        setAppliedSubscriptionStatusFilter(subscriptionStatusFilter)
        setAppliedSubscriptionOrgFilter(subscriptionOrgFilter)
        setAppliedExpiryFilter(expiryFilter)
    }

    const resetSubscriptionFilters = () => {
        setSubscriptionSearch('')
        setSubscriptionStatusFilter('ALL')
        setSubscriptionOrgFilter('ALL')
        setExpiryFilter('ALL')
        setSubscriptionPage(1)
        setAppliedSubscriptionSearch('')
        setAppliedSubscriptionStatusFilter('ALL')
        setAppliedSubscriptionOrgFilter('ALL')
        setAppliedExpiryFilter('ALL')
    }

    const applyOrderFilters = () => {
        setOrderPage(1)
        setAppliedOrderSearch(orderSearch)
        setAppliedOrderStatusFilter(orderStatusFilter)
        setAppliedOrderDateFrom(orderDateFrom)
        setAppliedOrderDateTo(orderDateTo)
    }

    const resetOrderFilters = () => {
        setOrderSearch('')
        setOrderStatusFilter('ALL')
        setOrderDateFrom('')
        setOrderDateTo('')
        setOrderPage(1)
        setAppliedOrderSearch('')
        setAppliedOrderStatusFilter('ALL')
        setAppliedOrderDateFrom('')
        setAppliedOrderDateTo('')
    }

    const applyRefundFilters = () => {
        setRefundPage(1)
        setAppliedRefundSearch(refundSearch)
        setAppliedRefundStatusFilter(refundStatusFilter)
        setAppliedRefundDateFrom(refundDateFrom)
        setAppliedRefundDateTo(refundDateTo)
    }

    const resetRefundFilters = () => {
        setRefundSearch('')
        setRefundStatusFilter('ALL')
        setRefundDateFrom('')
        setRefundDateTo('')
        setRefundPage(1)
        setAppliedRefundSearch('')
        setAppliedRefundStatusFilter('ALL')
        setAppliedRefundDateFrom('')
        setAppliedRefundDateTo('')
    }

    const applyAuditFilters = () => {
        setAuditPage(1)
        setAppliedAuditSearch(auditSearch)
        setAppliedAuditActionFilter(auditActionFilter)
        setAppliedAuditTargetTypeFilter(auditTargetTypeFilter)
        setAppliedAuditProviderFilter(auditProviderFilter)
        setAppliedAuditStatusFilter(auditStatusFilter)
        setAppliedAuditOrderNoFilter(auditOrderNoFilter)
        setAppliedAuditPaymentIdFilter(auditPaymentIdFilter)
        setAppliedAuditWebhookEventIdFilter(auditWebhookEventIdFilter)
        setAppliedAuditDateFrom(auditDateFrom)
        setAppliedAuditDateTo(auditDateTo)
    }

    const resetAuditFilters = () => {
        setAuditSearch('')
        setAuditActionFilter('ALL')
        setAuditTargetTypeFilter('ALL')
        setAuditProviderFilter('ALL')
        setAuditStatusFilter('ALL')
        setAuditOrderNoFilter('')
        setAuditPaymentIdFilter('')
        setAuditWebhookEventIdFilter('')
        setAuditDateFrom('')
        setAuditDateTo('')
        setAuditPage(1)
        setAppliedAuditSearch('')
        setAppliedAuditActionFilter('ALL')
        setAppliedAuditTargetTypeFilter('ALL')
        setAppliedAuditProviderFilter('ALL')
        setAppliedAuditStatusFilter('ALL')
        setAppliedAuditOrderNoFilter('')
        setAppliedAuditPaymentIdFilter('')
        setAppliedAuditWebhookEventIdFilter('')
        setAppliedAuditDateFrom('')
        setAppliedAuditDateTo('')
    }

    const handleEnterToApply = (event, applyFn) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyFn()
        }
    }

    const changeSubscriptionSort = (nextKey) => {
        if (subscriptionSortKey === nextKey) {
            setSubscriptionSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSubscriptionSortKey(nextKey)
        setSubscriptionSortDirection(nextKey === 'endsAt' ? 'asc' : 'asc')
    }

    const changeOrderSort = (nextKey) => {
        if (orderSortKey === nextKey) {
            setOrderSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setOrderSortKey(nextKey)
        setOrderSortDirection(nextKey === 'amount' ? 'desc' : 'desc')
    }

    const changeRefundSort = (nextKey) => {
        if (refundSortKey === nextKey) {
            setRefundSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setRefundSortKey(nextKey)
        setRefundSortDirection(nextKey === 'amount' ? 'desc' : 'desc')
    }

    const changeAuditSort = (nextKey) => {
        if (auditSortKey === nextKey) {
            setAuditSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setAuditSortKey(nextKey)
        setAuditSortDirection('desc')
    }

    const downloadSubscriptionsCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/subscriptions', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedSubscriptionSearch,
                        status: appliedSubscriptionStatusFilter,
                        orgName: appliedSubscriptionOrgFilter,
                        expiry: appliedExpiryFilter,
                        sortKey: subscriptionSortKey,
                        sortDirection: subscriptionSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 구독 데이터가 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['조직', '이름', '플랜', '과금모델', '만료일', '개인구독ID', '개인과금모델', '개인만료일', '팩스잔여', '팩스예약', '팩스사용', '문자잔여', '분석잔여', '상태'],
                    ...items.map((item) => [
                        item.orgName || '',
                        item.targetName || '',
                        item.planName || '',
                        formatBillingModeLabel(item.billingMode),
                        formatSubscriptionExpiry(item.endsAt),
                        item.individualSubscriptionId != null ? String(item.individualSubscriptionId) : '',
                        item.individualBillingMode != null ? formatBillingModeLabel(item.individualBillingMode) : '',
                        formatSubscriptionExpiry(item.individualEndsAt),
                        String(formatLimit(item.claimFaxRemaining)),
                        String(formatLimit(item.claimFaxReserved)),
                        String(formatLimit(item.claimFaxUsed)),
                        String(formatLimit(item.groupSmsRemaining)),
                        String(formatLimit(item.healthAnalysisRemaining)),
                        formatBillingStatusLabel(item.status, 'subscription'),
                    ]),
                ]
                const csv = '\uFEFF' + rows.map((row) => row.map(csvEscape).join(',')).join('\n')
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `billing_subscriptions_${new Date().toISOString().slice(0, 10)}.csv`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                show('구독 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const downloadOrdersCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/orders', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedOrderSearch,
                        status: appliedOrderStatusFilter,
                        dateFrom: appliedOrderDateFrom,
                        dateTo: appliedOrderDateTo,
                        sortKey: orderSortKey,
                        sortDirection: orderSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 주문 데이터가 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['주문번호', '구매자', '조직', '플랜', '금액', '상태', 'PG', '결제수단', 'PG거래번호', '결제일시'],
                    ...items.map((order) => [
                        order.orderNo || order.id || '',
                        order.buyerName || '',
                        order.orgName || order.targetType || '',
                        order.planName || '',
                        String(order.amount ?? ''),
                        order.status || '',
                        order.pgProvider || '',
                        order.paymentMethod || '',
                        order.pgTransactionId || order.pgOrderId || '',
                        formatDateTimeMinute(order.paidAt || order.createdAt) || '',
                    ]),
                ]
                downloadCsvFile(rows, `billing_orders_${new Date().toISOString().slice(0, 10)}.csv`)
            } catch (e) {
                show('주문 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const downloadRefundsCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/refunds', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedRefundSearch,
                        status: appliedRefundStatusFilter,
                        dateFrom: appliedRefundDateFrom,
                        dateTo: appliedRefundDateTo,
                        sortKey: refundSortKey,
                        sortDirection: refundSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 환불 데이터가 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['환불번호', '주문번호', '결제ID', '요청자', '금액', '상태', '사유', '요청시각'],
                    ...items.map((refund) => [
                        refund.refundNo || refund.id || '',
                        refund.orderNo || refund.orderId || '',
                        refund.paymentId || '',
                        refund.requestedBy || '',
                        String(refund.amount ?? ''),
                        refund.status || '',
                        refund.reason || '',
                        formatDateTimeMinute(refund.requestedAt || refund.updatedAt) || '',
                    ]),
                ]
                downloadCsvFile(rows, `billing_refunds_${new Date().toISOString().slice(0, 10)}.csv`)
            } catch (e) {
                show('환불 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const downloadAuditLogsCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/audit-logs', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedAuditSearch,
                        action: appliedAuditActionFilter,
                        targetType: appliedAuditTargetTypeFilter,
                        provider: appliedAuditProviderFilter,
                        status: appliedAuditStatusFilter,
                        orderNo: appliedAuditOrderNoFilter,
                        paymentId: appliedAuditPaymentIdFilter,
                        webhookEventId: appliedAuditWebhookEventIdFilter,
                        dateFrom: appliedAuditDateFrom,
                        dateTo: appliedAuditDateTo,
                        sortKey: auditSortKey,
                        sortDirection: auditSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 감사 이력이 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['행위자 이메일', '액션', '대상유형', '대상ID', '상태', 'PG', '주문번호', '결제ID', '웹훅ID', '사유', '시각'],
                    ...items.map((log) => [
                        log.actorEmail || '',
                        log.action || '',
                        log.targetType || '',
                        String(log.targetId ?? ''),
                        pickAuditStatus(log) || '',
                        log.provider || '',
                        log.orderNo || '',
                        String(log.paymentId ?? ''),
                        log.webhookEventId || '',
                        log.reason || '',
                        formatDateTimeMinute(log.createdAt) || '',
                    ]),
                ]
                downloadCsvFile(rows, `billing_audit_logs_${new Date().toISOString().slice(0, 10)}.csv`)
            } catch (e) {
                show('감사 이력 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const openBulkTrial = (revoke = false) => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 구독을 선택해주세요.', 'error')
            return
        }
        setBulkTrialDays(7)
        setBulkTrialReason(revoke ? '운영자 일괄 Trial 회수' : '운영자 일괄 Trial 지급')
        setBulkTrialModal({ revoke })
    }

    const submitBulkTrial = async () => {
        if (!bulkTrialModal) return
        try {
            setBulkTrialLoading(true)
            await api.post('/billing/admin/subscriptions/trial-bulk', {
                subscriptionIds: selectedSubscriptionIds,
                revoke: bulkTrialModal.revoke,
                days: Number(bulkTrialDays) || 7,
                reason: bulkTrialReason || (bulkTrialModal.revoke ? '운영자 일괄 Trial 회수' : '운영자 일괄 Trial 지급'),
            })
            show(bulkTrialModal.revoke ? '일괄 Trial 회수가 완료되었습니다.' : '일괄 Trial 지급이 완료되었습니다.', 'success')
            setBulkTrialModal(null)
            setSelectedSubscriptionIds([])
            await loadData()
        } catch (e) {
            show('일괄 Trial 처리에 실패했습니다.', 'error')
        } finally {
            setBulkTrialLoading(false)
        }
    }

    const submitBulkUpdate = async () => {
        if (!bulkUpdateModal) return
        const reason = bulkUpdateReason.trim()
        if (!reason) {
            show('변경 사유를 입력해주세요.', 'error')
            return
        }
        await updateSelectedSubscriptions({
            ...bulkUpdateModal.payload,
            reason,
        }, bulkUpdateModal.successMessage, bulkUpdateModal.errorMessage, () => {
            if (bulkUpdateModal.type === 'status') {
                setBulkStatus('')
            }
            if (bulkUpdateModal.type === 'plan') {
                setBulkPlanCode('')
            }
            setBulkUpdateReason('')
            setBulkUpdateModal(null)
        })
    }

    const openBulkCompose = () => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        const eligible = selectedSubscriptions.filter((s) => s.billingReady !== false)
        if (eligible.length === 0) {
            show('billing이 준비된 회원을 선택해주세요.', 'error')
            return
        }
        setBulkComposeStatus('')
        setBulkComposePlan('')
        setBulkComposeGrantDays('')
        setBulkComposeQuotaFeature('')
        setBulkComposeQuotaDelta('')
        setBulkComposeReason('운영자 일괄 적용')
        setBulkComposeOpen(true)
    }

    const submitBulkCompose = async () => {
        const reason = bulkComposeReason.trim()
        if (!reason) {
            show('변경 사유를 입력해주세요.', 'error')
            return
        }
        const statusVal = bulkComposeStatus.trim()
        const planVal = bulkComposePlan.trim()
        const grantDaysVal = parseInt(String(bulkComposeGrantDays).trim(), 10)
        const quotaDeltaRaw = String(bulkComposeQuotaDelta).trim()
        const quotaDeltaVal = quotaDeltaRaw === '' ? NaN : parseInt(quotaDeltaRaw, 10)
        const quotaFeatureVal = bulkComposeQuotaFeature.trim()
        const hasPatch = Boolean(statusVal || planVal)
        const hasGrant = !Number.isNaN(grantDaysVal) && grantDaysVal > 0
        const hasQuotaAdjust = quotaFeatureVal && !Number.isNaN(quotaDeltaVal) && quotaDeltaVal !== 0
        if (!hasPatch && !hasGrant && !hasQuotaAdjust) {
            show('구독 상태·플랜·구독일·할당량 조정 중 최소 한 항목을 지정해주세요.', 'error')
            return
        }
        const ids = selectedSubscriptions.filter((s) => s.billingReady !== false).map((s) => s.id)
        if (ids.length === 0) {
            show('적용할 대상이 없습니다.', 'error')
            return
        }
        try {
            setBulkComposeLoading(true)
            if (hasPatch) {
                const payload = { reason }
                if (statusVal) payload.status = statusVal
                if (planVal) payload.planCode = planVal
                await Promise.all(ids.map((id) => api.patch(`/billing/admin/subscriptions/${id}`, payload)))
            }
            if (hasGrant) {
                await Promise.all(ids.map((id) => (
                    api.post(`/billing/admin/subscriptions/${id}/grant-days`, {
                        days: grantDaysVal,
                        reason,
                    })
                )))
            }
            if (hasQuotaAdjust) {
                await Promise.all(ids.map((id) => (
                    api.post(`/billing/admin/subscriptions/${id}/quota-limit-adjust`, {
                        featureCode: quotaFeatureVal,
                        delta: quotaDeltaVal,
                        reason,
                    })
                )))
            }
            show('선택한 회원에게 일괄 적용했습니다.', 'success')
            setBulkComposeOpen(false)
            setBulkComposeStatus('')
            setBulkComposePlan('')
            setBulkComposeGrantDays('')
            setBulkComposeQuotaFeature('')
            setBulkComposeQuotaDelta('')
            setBulkComposeReason('')
            setSelectedSubscriptionIds([])
            await loadData()
        } catch (e) {
            show('일괄 적용에 실패했습니다.', 'error')
        } finally {
            setBulkComposeLoading(false)
        }
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl font-bold">{isDashboardView ? '대시보드' : '과금/구독 허브'}</h1>
                        <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor={subscriptionsManualAnchor} />
                    </div>
                    {isDashboardView ? (
                        <div className="mt-2">
                            <button
                                type="button"
                                className="text-sm font-medium text-brand-navy hover:underline"
                                onClick={() => navigate(billingAdminHubPath('status'))}
                            >
                                과금/구독 허브로 이동
                            </button>
                        </div>
                    ) : (
                        <div className="mt-2">
                            <button
                                type="button"
                                className="text-sm font-medium text-slate-600 hover:text-brand-navy hover:underline"
                                onClick={() => navigate('/subscriptions/dashboard')}
                            >
                                대시보드로 이동
                            </button>
                        </div>
                    )}
                </div>
                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={loadData}>새로고침</button>
            </div>

            {!isDashboardView && (
                <div className="mt-6 border-b">
                    <nav className="flex flex-wrap gap-x-6">
                        {hubTabMenus.map((tab) => (
                            <button
                                type="button"
                                key={tab.id}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeMenu === tab.id ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                                onClick={() => navigate(tab.path)}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {loading && !billingAdminListTabActive(activeMenu) && <div className="mt-8 text-slate-500">로딩 중...</div>}
            {!loading && error && !billingAdminListTabActive(activeMenu) && (
                <div className="mt-8 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {!loading && !error && activeMenu === 'dashboard' && (
                <div className="mt-6 space-y-6">
                    <DashboardSection title="회원" tone="blue">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <DashboardCard label="전체 회원" value={String(dashboard?.totalMembers ?? 0)} hint="GA_PROXY 제외 기준" tone="blue" />
                            <DashboardCard label="오늘 가입자" value={String(dashboard?.todayJoinedMembers ?? 0)} hint="당일 00:00 이후" tone="blue" />
                            <DashboardCard label="7일 내 가입자" value={String(dashboard?.joinedIn7Days ?? 0)} hint="최근 7일 누적" tone="blue" />
                            <DashboardCard label="30일 내 가입자" value={String(dashboard?.joinedIn30Days ?? 0)} hint="최근 30일 누적" tone="blue" />
                        </div>
                    </DashboardSection>

                    <DashboardSection title="구독" tone="violet">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <DashboardCard label="활성 구독" value={String(dashboard?.activeSubscriptions ?? 0)} hint="정상 과금/이용 중" tone="violet" />
                            <DashboardCard label="체험중 회원" value={String(dashboard?.trialSubscriptions ?? 0)} hint="TRIAL 상태" tone="violet" />
                            <DashboardCard label="구독 만료 수" value={String(dashboard?.expiredSubscriptions ?? 0)} hint="EXPIRED 상태" tone="violet" />
                            <DashboardCard label="7일 내 만료 예정" value={String(dashboard?.expiringIn7Days ?? 0)} hint="미리 대응 필요" tone="violet" />
                        </div>
                    </DashboardSection>

                    <DashboardSection title="결제" tone="emerald">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <DashboardCard label="오늘 결제 성공" value={String(dashboard?.todayPaymentSuccess ?? 0)} hint="PAID 건수" tone="emerald" />
                            <DashboardCard label="오늘 결제 실패" value={String(dashboard?.todayPaymentFailed ?? 0)} hint="FAILED 건수" tone="emerald" />
                            <DashboardCard label="오늘 결제 금액" value={currency(dashboard?.todayPaymentAmount ?? 0)} hint="결제 완료 금액 합계" tone="emerald" />
                            <DashboardCard label="환불 요청" value={String(dashboard?.pendingRefunds ?? 0)} hint="REQUESTED / APPROVED" tone="emerald" />
                        </div>
                    </DashboardSection>

                    <DashboardSection title="플랜별 구독 수" tone="amber">
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                            {Object.entries(dashboard?.planSubscriptionCounts || {}).map(([planName, count]) => (
                                <div key={planName} className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                                    <div className="text-sm font-medium text-amber-900">{planName}</div>
                                    <div className="mt-2 text-xl font-semibold text-slate-900">{count}</div>
                                </div>
                            ))}
                            {Object.keys(dashboard?.planSubscriptionCounts || {}).length === 0 && (
                                <div className="col-span-full rounded border border-dashed px-4 py-6 text-sm text-slate-500">
                                    표시할 플랜 집계가 없습니다.
                                </div>
                            )}
                        </div>
                    </DashboardSection>

                    <DashboardSection title="운영 처리 큐" tone="amber">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {(dashboard?.operationQueue || []).map((item) => (
                                <div key={item.code} className={`rounded-xl border px-4 py-3 ${Number(item.count || 0) > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                                    <div className="text-sm font-medium text-slate-900">{item.label}</div>
                                    <div className="mt-2 text-2xl font-semibold text-slate-900">{item.count ?? 0}</div>
                                    <div className="mt-1 text-xs text-slate-500">{Number(item.count || 0) > 0 ? '확인 필요' : '정상'}</div>
                                </div>
                            ))}
                            {(dashboard?.operationQueue || []).length === 0 && (
                                <div className="col-span-full rounded border border-dashed px-4 py-6 text-sm text-slate-500">
                                    처리할 운영 항목이 없습니다.
                                </div>
                            )}
                        </div>
                    </DashboardSection>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                        <DashboardSection title="프로모션 상태" tone="rose">
                            <div className="space-y-3 text-sm">
                                {(dashboard?.recentPromotions || []).slice(0, 3).map((promotion) => (
                                    <div key={promotion.id} className="rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="font-medium text-slate-900">{promotion.name || promotion.code || `프로모션 #${promotion.id}`}</div>
                                            <span className="rounded-full bg-white px-2.5 py-1 text-xs text-rose-700 ring-1 ring-rose-200">{promotion.status || '-'}</span>
                                        </div>
                                        <div className="mt-1 text-xs text-slate-500">{promotion.code || '코드 없음'} / {promotion.promotionType || '-'}</div>
                                        <div className="mt-1 text-slate-600">
                                            {(promotion.rewards || []).length > 0
                                                ? promotion.rewards.slice(0, 2).map((reward) => formatRewardLabel(reward)).join(', ')
                                                : '혜택 없음'}
                                        </div>
                                        <div className="mt-1 text-slate-500">
                                            {`${formatDate(promotion.validFrom) || '-'} ~ ${formatDate(promotion.validTo) || '-'}`}
                                        </div>
                                    </div>
                                ))}
                                {(dashboard?.recentPromotions || []).length === 0 && (
                                    <div className="rounded border border-dashed px-4 py-6 text-sm text-slate-500">
                                        표시할 최근 프로모션이 없습니다.
                                    </div>
                                )}
                            </div>
                        </DashboardSection>

                        <DashboardSection title="이력" tone="slate">
                            <div className="space-y-3 text-sm">
                                {(dashboard?.recentAudits || []).slice(0, 5).map((item) => (
                                    <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                                        <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div className="font-medium">{formatBillingAuditActionLabel(item.action)}</div>
                                            <div className="text-xs text-slate-500">{formatDateTimeMinute(item.createdAt)}</div>
                                        </div>
                                        <div className="mt-1 font-medium text-slate-800">{item.actorName || '-'}</div>
                                        <div className="mt-1 text-xs text-slate-500">{item.actorEmail || '-'}</div>
                                        <div className="mt-1 text-sm text-slate-700">{item.targetDisplayName || '-'}</div>
                                        <div className="mt-0.5 text-xs text-slate-500">{`${formatBillingAuditTargetLabel(item.targetType)}${item.targetId != null && item.targetId !== '' ? ` #${item.targetId}` : ''}`}</div>
                                        {item.reason && (
                                            <div className="mt-2 rounded bg-white px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
                                                {item.reason}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {(dashboard?.recentAudits || []).length === 0 && (
                                    <div className="rounded border border-dashed px-4 py-6 text-sm text-slate-500">
                                        표시할 최근 이력이 없습니다.
                                    </div>
                                )}
                            </div>
                        </DashboardSection>
                    </div>
                </div>
            )}

            {activeMenu === 'status' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold">구독 상태</h2>
                            <p className="mt-1 text-sm text-slate-500">전체 회원 기준으로 구독 상태를 조회합니다. <code>GA_PROXY</code> 생성 회원은 제외됩니다.</p>
                            <p className="mt-1 text-xs text-amber-800/90">목록은 서버에서 전체 회원을 집계한 뒤 페이징합니다. 회원 수가 많으면 첫 로딩이 길어질 수 있습니다.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <select
                                className="rounded border px-3 py-2 text-xs hover:bg-white"
                                value={subscriptionPageSize}
                                onChange={(e) => {
                                    const n = Number(e.target.value)
                                    setSubscriptionPageSize(SUBSCRIPTION_PAGE_SIZES.includes(n) ? n : 10)
                                }}
                            >
                                {SUBSCRIPTION_PAGE_SIZES.map((sz) => (
                                    <option key={sz} value={sz}>{sz}개씩</option>
                                ))}
                            </select>
                            <button type="submit" form="subscription-status-filter-form" className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">검색</button>
                            <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-white" onClick={resetSubscriptionFilters}>필터 초기화</button>
                            <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-white" onClick={downloadSubscriptionsCsv}>CSV 다운로드</button>
                        </div>
                    </div>

                    <form
                        id="subscription-status-filter-form"
                        className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4"
                        onSubmit={(e) => {
                            e.preventDefault()
                            applySubscriptionFilters()
                        }}
                    >
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="조직/이름/이메일/플랜 검색"
                            value={subscriptionSearch}
                            onChange={(e) => setSubscriptionSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applySubscriptionFilters)}
                        />
                        <select className="rounded border px-3 py-2 text-sm" value={subscriptionStatusFilter} onChange={(e) => setSubscriptionStatusFilter(e.target.value)}>
                            <option value="ALL">전체 상태</option>
                            <option value="TRIAL">{formatBillingStatusLabel('TRIAL', 'subscription')}</option>
                            <option value="ACTIVE">{formatBillingStatusLabel('ACTIVE', 'subscription')}</option>
                            <option value="SUSPENDED">{formatBillingStatusLabel('SUSPENDED', 'subscription')}</option>
                            <option value="EXPIRED">{formatBillingStatusLabel('EXPIRED', 'subscription')}</option>
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={subscriptionOrgFilter} onChange={(e) => setSubscriptionOrgFilter(e.target.value)}>
                            <option value="ALL">전체 조직</option>
                            <option value="미지정">미지정</option>
                            {orgOptions.map((orgName) => (
                                <option key={orgName} value={orgName}>{orgName}</option>
                            ))}
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={expiryFilter} onChange={(e) => setExpiryFilter(e.target.value)}>
                            <option value="ALL">전체 만료조건</option>
                            <option value="WITHIN_7_DAYS">7일 이내 만료</option>
                            <option value="WITHIN_30_DAYS">30일 이내 만료</option>
                            <option value="EXPIRED">만료됨</option>
                            <option value="NO_END_AT">만료일 없음</option>
                        </select>
                    </form>

                    {subscriptionLoadError && (
                        <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{subscriptionLoadError}</div>
                    )}

                    <div className="mt-4 rounded border bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                            <div className="text-slate-600">
                                검색 결과 <span className="font-semibold text-slate-900">{subscriptionTotalCount}</span>건 / 선택된 회원 <span className="font-semibold text-slate-900">{selectedSubscriptionIds.length}</span>건 / 현재 페이지에서 선택 <span className="font-semibold text-slate-900">{pageSelectedCount}</span>건
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50"
                                    disabled={billingBootstrapLoading}
                                    onClick={() => bootstrapMissingBilling({ dryRun: true })}
                                >
                                    누락 billing dry-run
                                </button>
                                <button
                                    type="button"
                                    className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50"
                                    disabled={billingBootstrapLoading}
                                    onClick={openBillingBootstrapModal}
                                >
                                    누락 billing 생성
                                </button>
                                <button
                                    type="button"
                                    className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50"
                                    disabled={selectedSubscriptionIds.length === 0}
                                    onClick={() => setSelectedSubscriptionIds([])}
                                >
                                    선택 해제
                                </button>
                            </div>
                        </div>

                        {selectedSubscriptionIds.length > 0 && (
                            <div className="mt-3 rounded border bg-white px-4 py-3 text-xs text-slate-600">
                                선택 회원:
                                {' '}
                                <span className="font-medium text-slate-900">
                                    {selectedSubscriptions.slice(0, 3).map((item) => item.targetName).join(', ')}
                                    {selectedSubscriptions.length > 3 ? ` 외 ${selectedSubscriptions.length - 3}명` : ''}
                                </span>
                            </div>
                        )}

                        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[repeat(7,minmax(0,1fr))]">
                            <button type="button" className="rounded border bg-white px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50" disabled={!singleSelectedSubscription || quotaDetailLoading} onClick={openSelectedQuota}>{quotaDetailLoading ? '할당량 로딩 중...' : '할당량 상세'}</button>
                            <button type="button" className="rounded border bg-white px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50" disabled={!selectedSubscriptions.some((s) => s.billingReady !== false)} onClick={openSelectedGrantDays}>기간 부여</button>
                            <button type="button" className="rounded border bg-white px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50" disabled={!singleSelectedSubscription} onClick={openSelectedTrialHistory}>Trial 이력</button>
                            <button type="button" className="rounded border bg-white px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || bulkActionLoading} onClick={() => openBulkTrial(false)}>선택 Trial 지급</button>
                            <button type="button" className="rounded border bg-white px-3 py-2 text-xs hover:bg-slate-100 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || bulkActionLoading} onClick={() => openBulkTrial(true)}>선택 Trial 회수</button>
                            <button type="button" className="rounded border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-medium text-teal-900 hover:bg-teal-100 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || bulkComposeLoading} onClick={openBulkCompose}>일괄 적용</button>
                            <div className="rounded border border-dashed bg-white px-3 py-2 text-xs text-slate-500">
                                상태/플랜만 바꿀 때는 아래 일괄 변경을 사용할 수 있습니다.
                            </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
                            <div className="flex flex-wrap items-center gap-2 rounded border bg-white p-3">
                                <span className="text-xs font-medium text-slate-600">선택 상태 변경</span>
                                <select className="rounded border px-3 py-2 text-xs" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
                                    <option value="">상태 선택</option>
                                    <option value="TRIAL">{formatBillingStatusLabel('TRIAL', 'subscription')}</option>
                                    <option value="ACTIVE">{formatBillingStatusLabel('ACTIVE', 'subscription')}</option>
                                    <option value="SUSPENDED">{formatBillingStatusLabel('SUSPENDED', 'subscription')}</option>
                                    <option value="EXPIRED">{formatBillingStatusLabel('EXPIRED', 'subscription')}</option>
                                </select>
                                <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || !bulkStatus || bulkActionLoading} onClick={openBulkStatusModal}>
                                    변경 확인
                                </button>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 rounded border bg-white p-3">
                                <span className="text-xs font-medium text-slate-600">선택 플랜 변경</span>
                                <select className="rounded border px-3 py-2 text-xs" value={bulkPlanCode} onChange={(e) => setBulkPlanCode(e.target.value)}>
                                    <option value="">플랜 선택</option>
                                    {plans.map((plan) => (
                                        <option key={plan.planCode} value={plan.planCode}>{plan.name}</option>
                                    ))}
                                </select>
                                <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || !bulkPlanCode || bulkActionLoading} onClick={openBulkPlanModal}>
                                    변경 확인
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {subscriptionListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
                        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={pagedSubscriptions.filter((item) => item.billingReady !== false).length > 0 && pagedSubscriptions.filter((item) => item.billingReady !== false).every((item) => selectedSubscriptionIds.includes(item.id))}
                                            onChange={toggleAllSubscriptionSelection}
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'orgName')} onClick={() => changeSubscriptionSort('orgName')}>
                                            조직 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'orgName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'targetName')} onClick={() => changeSubscriptionSort('targetName')}>
                                            이름 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'targetName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'planName')} onClick={() => changeSubscriptionSort('planName')}>
                                            플랜(티어) {sortMark(subscriptionSortKey, subscriptionSortDirection, 'planName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'billingMode')} onClick={() => changeSubscriptionSort('billingMode')}>
                                            과금모델 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'billingMode')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'endsAt')} onClick={() => changeSubscriptionSort('endsAt')}>
                                            만료일 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'endsAt')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">개인 구독 만료</th>
                                    <th className="px-4 py-3 text-left">할당량</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'status')} onClick={() => changeSubscriptionSort('status')}>
                                            상태 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'status')}
                                        </button>
                                    </th>
                </tr>
            </thead>
            <tbody>
                                {pagedSubscriptions.map((item) => (
                                    <tr key={item.id} className="border-t">
                                        <td className="px-4 py-3">
                                            <input type="checkbox" checked={selectedSubscriptionIds.includes(item.id)} disabled={item.billingReady === false} onChange={() => toggleSubscriptionSelection(item.id)} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{item.orgName || '미지정'}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{item.targetName}</div>
                                            <div className="text-xs text-slate-500">{item.userEmail || '-'}</div>
                                            {item.billingReady === false && (
                                                <div className="text-xs text-amber-600">구독 미시작</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">{item.planName}</td>
                                        <td className="px-4 py-3">
                                            <div>{formatBillingModeLabel(item.billingMode)}</div>
                                            <div className="mt-1 text-xs text-slate-500">권한 출처: {item.entitlementSourceLabel || item.entitlementSource || '-'}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>{formatDate(item.endsAt) || '-'}</div>
                                            <div className="text-xs text-slate-500">{formatExpiryRemaining(item.endsAt)}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-700">
                                            {item.individualSubscriptionId != null ? (
                                                <>
                                                    <div className="text-sm text-slate-900">{formatDate(item.individualEndsAt) || '-'}</div>
                                                    <div className="mt-0.5 text-slate-500">{formatExpiryRemaining(item.individualEndsAt)}</div>
                                                    <div className="mt-1 text-slate-500">
                                                        {formatBillingModeLabel(item.individualBillingMode)} · {item.individualPlanName || '-'}
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-600">
                                            <QuotaSummaryList item={item} />
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge value={item.status} type="subscription" /></td>
                    </tr>
                ))}
                                {subscriptions.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={9}>조건에 맞는 회원 구독이 없습니다.</td>
                                    </tr>
                                )}
            </tbody>
        </table>
                    </div>

                    <TablePager
                        totalCount={subscriptionTotalCount}
                        page={subscriptionPage}
                        pageSize={subscriptionPageSize}
                        totalPages={subscriptionTotalPages}
                        onPrev={() => setSubscriptionPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setSubscriptionPage((prev) => Math.min(subscriptionTotalPages, prev + 1))}
                    />
                </div>
            )}

            {activeMenu === 'orders' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <h2 className="text-lg font-semibold">주문/결제</h2>
                    {orderLoadError && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{orderLoadError}</div>
                    )}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="주문번호/구매자/조직/PG 검색"
                            value={orderSearch}
                            onChange={(e) => setOrderSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyOrderFilters)}
                        />
                        <select className="rounded border px-3 py-2 text-sm" value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
                            <option value="ALL">전체 상태</option>
                            <option value="PENDING">{formatBillingStatusLabel('PENDING', 'order')}</option>
                            <option value="PAYMENT_PENDING">{formatBillingStatusLabel('PAYMENT_PENDING', 'order')}</option>
                            <option value="PAID">{formatBillingStatusLabel('PAID', 'order')}</option>
                            <option value="FAILED">{formatBillingStatusLabel('FAILED', 'order')}</option>
                            <option value="CANCELED">{formatBillingStatusLabel('CANCELED', 'order')}</option>
                            <option value="REFUNDED">{formatBillingStatusLabel('REFUNDED', 'order')}</option>
                        </select>
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={orderDateFrom} onChange={(e) => setOrderDateFrom(e.target.value)} />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={orderDateTo} onChange={(e) => setOrderDateTo(e.target.value)} />
                        <div className="flex gap-2">
                            <select className="rounded border px-3 py-2 text-sm" value={orderPageSize} onChange={(e) => setOrderPageSize(Number(e.target.value) || 10)}>
                                <option value={10}>10개씩</option>
                                <option value={20}>20개씩</option>
                                <option value={50}>50개씩</option>
                                <option value={100}>100개씩</option>
                            </select>
                            <button className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={applyOrderFilters}>검색</button>
                            <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={resetOrderFilters}>필터 초기화</button>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                        <div>검색 결과 <span className="font-semibold text-slate-900">{orderTotalCount}</span>건</div>
                        <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={downloadOrdersCsv}>CSV 다운로드</button>
                    </div>
                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {orderListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                                    <th className="px-4 py-3 text-left">주문번호</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('buyerName')}>
                                            구매자 {sortMark(orderSortKey, orderSortDirection, 'buyerName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('planName')}>
                                            플랜 {sortMark(orderSortKey, orderSortDirection, 'planName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('amount')}>
                                            금액 {sortMark(orderSortKey, orderSortDirection, 'amount')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('status')}>
                                            상태 {sortMark(orderSortKey, orderSortDirection, 'status')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">쿠폰</th>
                                    <th className="px-4 py-3 text-left">PG/결제수단</th>
                                    <th className="px-4 py-3 text-left">영수증</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('paidAt')}>
                                            결제일시 {sortMark(orderSortKey, orderSortDirection, 'paidAt')}
                                        </button>
                                    </th>
                    <th className="px-4 py-3 text-left">액션</th>
                </tr>
            </thead>
            <tbody>
                                {pagedOrders.map((order) => (
                                    <tr key={order.id} className="border-t">
                                        <td className="px-4 py-3">{order.orderNo || order.id}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{order.buyerName || '-'}</div>
                                            <div className="text-xs text-slate-500">{order.orgName || order.targetType || '-'}</div>
                                        </td>
                                        <td className="px-4 py-3">{order.planName || '-'}</td>
                                        <td className="px-4 py-3">{currency(order.amount)}</td>
                                        <td className="px-4 py-3"><StatusBadge value={order.status} type="order" /></td>
                                        <td className="px-4 py-3">{formatCouponReservation(order)}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-xs text-slate-600">
                                                <div>{order.pgProvider || '-'}</div>
                                                <div>{order.paymentMethod || '-'}</div>
                                                <div>{order.pgTransactionId || order.pgOrderId || '-'}</div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{renderExternalLink(order.receiptUrl, '보기')}</td>
                                        <td className="px-4 py-3">{formatDateTimeMinute(order.paidAt || order.createdAt)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-2">
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openOrderDetail(order.id)}>상세</button>
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => reconcileOrder(order.id)}>재검증</button>
                                            </div>
                                        </td>
                    </tr>
                ))}
                                {orders.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={10}>주문 데이터가 없습니다.</td>
                                    </tr>
                                )}
            </tbody>
        </table>
                    </div>
                    <TablePager
                        totalCount={orderTotalCount}
                        page={orderPage}
                        pageSize={orderPageSize}
                        totalPages={orderTotalPages}
                        onPrev={() => setOrderPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setOrderPage((prev) => Math.min(orderTotalPages, prev + 1))}
                    />
                </div>
            )}

            {activeMenu === 'refunds' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <h2 className="text-lg font-semibold">환불/취소</h2>
                    {refundLoadError && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{refundLoadError}</div>
                    )}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="환불번호/주문번호/요청자 검색"
                            value={refundSearch}
                            onChange={(e) => setRefundSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyRefundFilters)}
                        />
                        <select className="rounded border px-3 py-2 text-sm" value={refundStatusFilter} onChange={(e) => setRefundStatusFilter(e.target.value)}>
                            <option value="ALL">전체 상태</option>
                            <option value="REQUESTED">{formatBillingStatusLabel('REQUESTED', 'refund')}</option>
                            <option value="APPROVED">{formatBillingStatusLabel('APPROVED', 'refund')}</option>
                            <option value="REJECTED">{formatBillingStatusLabel('REJECTED', 'refund')}</option>
                            <option value="COMPLETED">{formatBillingStatusLabel('COMPLETED', 'refund')}</option>
                        </select>
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={refundDateFrom} onChange={(e) => setRefundDateFrom(e.target.value)} />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={refundDateTo} onChange={(e) => setRefundDateTo(e.target.value)} />
                        <div className="flex gap-2">
                            <select className="rounded border px-3 py-2 text-sm" value={refundPageSize} onChange={(e) => setRefundPageSize(Number(e.target.value) || 10)}>
                                <option value={10}>10개씩</option>
                                <option value={20}>20개씩</option>
                                <option value={50}>50개씩</option>
                                <option value={100}>100개씩</option>
                            </select>
                            <button className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={applyRefundFilters}>검색</button>
                            <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={resetRefundFilters}>필터 초기화</button>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                        <div>검색 결과 <span className="font-semibold text-slate-900">{refundTotalCount}</span>건</div>
                        <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={downloadRefundsCsv}>CSV 다운로드</button>
                    </div>
                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {refundListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('refundNo')}>
                                            환불번호 {sortMark(refundSortKey, refundSortDirection, 'refundNo')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">주문번호</th>
                                    <th className="px-4 py-3 text-left">결제ID</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('requestedBy')}>
                                            요청자 {sortMark(refundSortKey, refundSortDirection, 'requestedBy')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('amount')}>
                                            금액 {sortMark(refundSortKey, refundSortDirection, 'amount')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('status')}>
                                            상태 {sortMark(refundSortKey, refundSortDirection, 'status')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">사유</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('createdAt')}>
                                            생성시각 {sortMark(refundSortKey, refundSortDirection, 'createdAt')}
                                        </button>
                                    </th>
                    <th className="px-4 py-3 text-left">액션</th>
                </tr>
            </thead>
            <tbody>
                                {pagedRefunds.map((refund) => (
                                    <tr key={refund.id} className="border-t">
                                        <td className="px-4 py-3">{refund.refundNo || refund.id}</td>
                                        <td className="px-4 py-3">{refund.orderNo || refund.orderId || '-'}</td>
                                        <td className="px-4 py-3">{refund.paymentId || '-'}</td>
                                        <td className="px-4 py-3">{refund.requestedBy || '-'}</td>
                                        <td className="px-4 py-3">{currency(refund.amount)}</td>
                                        <td className="px-4 py-3"><StatusBadge value={refund.status} type="refund" /></td>
                                        <td className="px-4 py-3">{refund.reason || '-'}</td>
                                        <td className="px-4 py-3">{formatDateTimeMinute(refund.requestedAt || refund.updatedAt)}</td>
                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-2">
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => updateRefund(refund.id, 'approve')}>승인</button>
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => updateRefund(refund.id, 'reject')}>반려</button>
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => updateRefund(refund.id, 'complete')}>완료</button>
                                            </div>
                        </td>
                    </tr>
                ))}
                                {refunds.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={9}>환불 데이터가 없습니다.</td>
                                    </tr>
                                )}
            </tbody>
        </table>
            </div>
                    <TablePager
                        totalCount={refundTotalCount}
                        page={refundPage}
                        pageSize={refundPageSize}
                        totalPages={refundTotalPages}
                        onPrev={() => setRefundPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setRefundPage((prev) => Math.min(refundTotalPages, prev + 1))}
                    />
        </div>
            )}

            {(activeMenu === 'audit' || activeMenu === 'billingLogs') && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <h2 className="text-lg font-semibold">{activeMenu === 'billingLogs' ? '과금로그' : '이력관리'}</h2>
                    <p className="mt-1 text-sm text-slate-500">결제, 환불, 웹훅, 구독 활성화 감사로그를 상태와 PG, 주문/결제 식별자로 세부 필터링합니다.</p>
                    {auditLoadError && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{auditLoadError}</div>
                    )}
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="행위자/액션/대상/사유/IP 검색"
                            value={auditSearch}
                            onChange={(e) => setAuditSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <select className="rounded border px-3 py-2 text-sm" value={auditActionFilter} onChange={(e) => setAuditActionFilter(e.target.value)}>
                            <option value="ALL">전체 액션</option>
                            {auditActionOptions.map((action) => (
                                <option key={action} value={action}>{formatBillingAuditActionLabel(action)}</option>
                            ))}
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={auditTargetTypeFilter} onChange={(e) => setAuditTargetTypeFilter(e.target.value)}>
                            <option value="ALL">전체 대상</option>
                            {auditTargetTypeOptions.map((targetType) => (
                                <option key={targetType} value={targetType}>{formatBillingAuditTargetLabel(targetType)}</option>
                            ))}
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={auditStatusFilter} onChange={(e) => setAuditStatusFilter(e.target.value)}>
                            <option value="ALL">전체 상태</option>
                            {auditStatusOptions.map((status) => (
                                <option key={status} value={status}>{formatBillingStatusLabel(status)}</option>
                            ))}
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={auditProviderFilter} onChange={(e) => setAuditProviderFilter(e.target.value)}>
                            <option value="ALL">전체 PG</option>
                            {auditProviderOptions.map((provider) => (
                                <option key={provider} value={provider}>{provider}</option>
                            ))}
                        </select>
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="주문번호"
                            value={auditOrderNoFilter}
                            onChange={(e) => setAuditOrderNoFilter(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="결제ID"
                            value={auditPaymentIdFilter}
                            onChange={(e) => setAuditPaymentIdFilter(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="웹훅 이벤트ID"
                            value={auditWebhookEventIdFilter}
                            onChange={(e) => setAuditWebhookEventIdFilter(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={auditDateFrom} onChange={(e) => setAuditDateFrom(e.target.value)} />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={auditDateTo} onChange={(e) => setAuditDateTo(e.target.value)} />
                        <div className="flex gap-2">
                            <select className="rounded border px-3 py-2 text-sm" value={auditPageSize} onChange={(e) => setAuditPageSize(Number(e.target.value) || 10)}>
                                <option value={10}>10개씩</option>
                                <option value={20}>20개씩</option>
                                <option value={50}>50개씩</option>
                                <option value={100}>100개씩</option>
                        </select>
                            <button className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={applyAuditFilters}>검색</button>
                            <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={resetAuditFilters}>필터 초기화</button>
                    </div>
                </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                        <div>검색 결과 <span className="font-semibold text-slate-900">{auditTotalCount}</span>건</div>
                        <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={downloadAuditLogsCsv}>CSV 다운로드</button>
                    </div>
                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {auditListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('actorEmail')}>
                                            행위자 {sortMark(auditSortKey, auditSortDirection, 'actorEmail')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('action')}>
                                            액션 {sortMark(auditSortKey, auditSortDirection, 'action')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('target')}>
                                            대상 {sortMark(auditSortKey, auditSortDirection, 'target')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">상태</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('provider')}>
                                            PG {sortMark(auditSortKey, auditSortDirection, 'provider')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('orderNo')}>
                                            주문번호 {sortMark(auditSortKey, auditSortDirection, 'orderNo')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">사유</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('createdAt')}>
                                            시각 {sortMark(auditSortKey, auditSortDirection, 'createdAt')}
                                        </button>
                                    </th>
                        </tr>
                    </thead>
                    <tbody>
                                {pagedAuditLogs.map((log, index) => (
                                    <tr
                                        key={`${log.id || log.createdAt || 'audit'}-${index}`}
                                        className="cursor-pointer border-t hover:bg-slate-50/90"
                                        onClick={() => setSelectedAuditLog(log)}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900">{log.actorName || '-'}</div>
                                            <div className="text-xs text-slate-500 break-all">{log.actorEmail || '-'}</div>
                                        </td>
                                        <td className="px-4 py-3">{formatBillingAuditActionLabel(log.action)}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900">{log.targetDisplayName || '-'}</div>
                                            <div className="text-xs text-slate-500">
                                                {`${formatBillingAuditTargetLabel(log.targetType)}${log.targetId != null && log.targetId !== '' ? ` #${log.targetId}` : ''}`}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {pickAuditStatus(log) ? (
                                                <span className={`rounded-full px-2 py-1 text-xs ${billingStatusToneClass(pickAuditStatus(log))}`}>
                                                    {formatBillingStatusLabel(pickAuditStatus(log))}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3">{log.provider || '-'}</td>
                                        <td className="px-4 py-3">
                                            <div>{log.orderNo || '-'}</div>
                                            <div className="text-xs text-slate-500">{log.paymentId ? `payment#${log.paymentId}` : log.webhookEventId || ''}</div>
                                        </td>
                                        <td className="max-w-xs truncate px-4 py-3" title={log.reason || ''}>{log.reason || '-'}</td>
                                        <td className="px-4 py-3">{formatDateTimeMinute(log.createdAt)}</td>
                            </tr>
                        ))}
                                {auditLogs.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={8}>감사 이력이 없습니다.</td>
                                    </tr>
                                )}
                    </tbody>
                </table>
                    </div>
                    <TablePager
                        totalCount={auditTotalCount}
                        page={auditPage}
                        pageSize={auditPageSize}
                        totalPages={auditTotalPages}
                        onPrev={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setAuditPage((prev) => Math.min(auditTotalPages, prev + 1))}
                    />
                </div>
            )}

            {!loading && !error && activeMenu === 'coupons' && (
                <div className="mt-6 space-y-6">
                    <div className="rounded border bg-white p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h2 className="text-lg font-semibold">{editingPromotionId ? '프로모션 수정' : '프로모션 생성'}</h2>
                                <p className="mt-1 text-sm text-slate-500">쿠폰, 추천인, 이벤트성 reward를 공통 구조로 등록합니다.</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                    disabled={couponReservationExpiryLoading}
                                    onClick={runCouponReservationExpiry}
                                >
                                    쿠폰 예약 만료 실행
                                </button>
                                {editingPromotionId && (
                                    <button
                                        className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                                        onClick={resetPromotionForm}
                                    >
                                        새로 작성
                                    </button>
                                )}
                                <button
                                    className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50"
                                    disabled={promotionSaving}
                                    onClick={submitPromotion}
                                >
                                    프로모션 저장
                                </button>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 text-sm">
                            <FormField label="프로모션 유형">
                                <select className="w-full rounded border px-3 py-2" value={promotionForm.promotionType} onChange={(e) => updatePromotionForm('promotionType', e.target.value)}>
                                    <option value="COUPON">쿠폰</option>
                                    <option value="REFERRAL">추천인</option>
                                    <option value="CAMPAIGN">캠페인</option>
                                    <option value="OPERATOR_GRANT">운영자 지급</option>
                        </select>
                            </FormField>
                            <FormField label="트리거">
                                <select className="w-full rounded border px-3 py-2" value={promotionForm.triggerType} onChange={(e) => updatePromotionForm('triggerType', e.target.value)}>
                                    <option value="CODE_REDEEM">코드 사용</option>
                                    <option value="SIGNUP_APPROVED">가입 승인</option>
                                    <option value="FIRST_PAID_ORDER">첫 결제 완료</option>
                                    <option value="MANUAL">수동</option>
                                </select>
                            </FormField>
                            <FormField label="코드">
                                <input className="w-full rounded border px-3 py-2" value={promotionForm.code} onChange={(e) => updatePromotionForm('code', e.target.value.toUpperCase())} placeholder="WELCOME2026" />
                            </FormField>
                            <FormField label="상태">
                                <select className="w-full rounded border px-3 py-2" value={promotionForm.status} onChange={(e) => updatePromotionForm('status', e.target.value)}>
                                    <option value="ACTIVE">ACTIVE</option>
                                    <option value="INACTIVE">INACTIVE</option>
                                </select>
                            </FormField>
                            <FormField label="이름">
                                <input className="w-full rounded border px-3 py-2" value={promotionForm.name} onChange={(e) => updatePromotionForm('name', e.target.value)} placeholder="가입 축하 쿠폰" />
                            </FormField>
                            <FormField label="사용자당 한도">
                                <input className="w-full rounded border px-3 py-2" type="number" min="1" value={promotionForm.perUserLimit} onChange={(e) => updatePromotionForm('perUserLimit', e.target.value)} placeholder="1" />
                            </FormField>
                            <FormField label="전체 한도">
                                <input className="w-full rounded border px-3 py-2" type="number" min="1" value={promotionForm.totalLimit} onChange={(e) => updatePromotionForm('totalLimit', e.target.value)} placeholder="1000" />
                            </FormField>
                            <FormField label="설명">
                                <input className="w-full rounded border px-3 py-2" value={promotionForm.description} onChange={(e) => updatePromotionForm('description', e.target.value)} placeholder="대상/용도 메모" />
                            </FormField>
                    </div>
                        <div className="mt-5">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-medium">Rewards</div>
                                <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={addPromotionReward}>reward 추가</button>
                            </div>
                            <div className="mt-3 space-y-3">
                                {promotionForm.rewards.map((reward, index) => (
                                    <div key={`reward-${index}`} className="grid grid-cols-1 gap-3 rounded border bg-slate-50 p-4 md:grid-cols-5">
                                        <select className="rounded border px-3 py-2 text-sm" value={reward.rewardType} onChange={(e) => updatePromotionReward(index, 'rewardType', e.target.value)}>
                                            <option value="POINT_GRANT">{formatPromotionRewardTypeLabel('POINT_GRANT')}</option>
                                            <option value="TRIAL_START">{formatPromotionRewardTypeLabel('TRIAL_START')}</option>
                                            <option value="TRIAL_DAYS_EXTEND">{formatPromotionRewardTypeLabel('TRIAL_DAYS_EXTEND')}</option>
                                            <option value="SUBSCRIPTION_DAYS_EXTEND">{formatPromotionRewardTypeLabel('SUBSCRIPTION_DAYS_EXTEND')}</option>
                                            <option value="AMOUNT_DISCOUNT">{formatPromotionRewardTypeLabel('AMOUNT_DISCOUNT')}</option>
                                            <option value="PERCENT_DISCOUNT">{formatPromotionRewardTypeLabel('PERCENT_DISCOUNT')}</option>
                                        </select>
                                        <select className="rounded border px-3 py-2 text-sm" value={reward.targetRole} onChange={(e) => updatePromotionReward(index, 'targetRole', e.target.value)}>
                                            <option value="REDEEMER">{formatPromotionRewardTargetRoleLabel('REDEEMER')}</option>
                                            <option value="REFERRER">{formatPromotionRewardTargetRoleLabel('REFERRER')}</option>
                                            <option value="REFEREE">{formatPromotionRewardTargetRoleLabel('REFEREE')}</option>
                                        </select>
                                        <input className="rounded border px-3 py-2 text-sm" type="number" min="0" value={reward.value} onChange={(e) => updatePromotionReward(index, 'value', e.target.value)} placeholder="값" />
                                        <input className="rounded border px-3 py-2 text-sm" type="number" min="0" value={reward.expireDays} onChange={(e) => updatePromotionReward(index, 'expireDays', e.target.value)} placeholder="포인트 만료일" disabled={reward.rewardType !== 'POINT_GRANT'} />
                                        <button className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50" disabled={promotionForm.rewards.length <= 1} onClick={() => removePromotionReward(index)}>삭제</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <AdminTable
                        title="등록된 프로모션"
                        columns={['코드', '이름', '유형/트리거', '상태', 'Reward', '한도', '사용현황', '생성일', '액션']}
                        rows={promotions.map((promotion) => [
                            promotion.code || '-',
                            promotion.name || '-',
                            <div key={`promotion-type-${promotion.id}`} className="text-xs text-slate-600">
                                <div>{promotion.promotionType}</div>
                                <div>{promotion.triggerType}</div>
                            </div>,
                            <StatusBadge key={`promotion-status-${promotion.id}`} value={promotion.status} type="default" />,
                            <div key={`promotion-reward-${promotion.id}`} className="space-y-1 text-xs text-slate-600">
                                {(promotion.rewards || []).map((reward) => (
                                    <div key={reward.id || `${promotion.id}-${reward.rewardType}`}>
                                        {formatRewardLabel(reward)}
                                    </div>
                                ))}
                            </div>,
                            `${promotion.perUserLimit || '-'} / ${promotion.totalLimit || '-'}`,
                            `${promotion.usedCount || 0}${promotion.remainingCount !== null && promotion.remainingCount !== undefined ? ` / 남음 ${promotion.remainingCount}` : ''}`,
                            formatDateTimeMinute(promotion.createdAt),
                            <div key={`promotion-actions-${promotion.id}`} className="flex flex-wrap gap-2">
                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => loadPromotionToForm(promotion)}>
                                    수정
                                </button>
                                <button
                                    className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                                    onClick={() => togglePromotionStatus(promotion, promotion.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                                >
                                    {promotion.status === 'ACTIVE' ? '비활성화' : '재활성화'}
                                </button>
                            </div>,
                        ])}
                        emptyText="등록된 프로모션이 없습니다."
                    />
                </div>
            )}
            {!loading && !error && activeMenu === 'points' && (
                <div className="mt-6 space-y-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-3">
                            <InfoBlock label="지갑 수" value={String(pointOverview?.walletCount ?? 0)} />
                            <InfoBlock label="사용자 지갑 수" value={String(pointOverview?.userWalletCount ?? 0)} />
                            <InfoBlock label="전체 잔액" value={`${(pointOverview?.totalBalance || 0).toLocaleString()}P`} />
                        </div>
                        <button
                            className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                            disabled={pointExpiryLoading}
                            onClick={runPointExpiry}
                        >
                            포인트 만료 실행
                        </button>
                    </div>
                    <AdminTable
                        title="상위 포인트 보유자"
                        columns={['이름', '이메일', '추천코드', '잔액', '상태', '수정일']}
                        rows={(pointOverview?.wallets || []).map((wallet) => [
                            wallet.ownerName || '-',
                            wallet.ownerEmail || '-',
                            wallet.referralCode || '-',
                            `${Number(wallet.balance || 0).toLocaleString()}P`,
                            wallet.status || '-',
                            formatDateTimeMinute(wallet.updatedAt),
                        ])}
                        emptyText="포인트 지갑이 없습니다."
                    />
                    <AdminTable
                        title="최근 포인트 거래"
                        columns={['사용자', '유형', '참조', '증감', '잔액', '메모', '일시']}
                        rows={(pointOverview?.recentTransactions || []).map((tx) => [
                            tx.ownerName || tx.ownerEmail || '-',
                            tx.txType || '-',
                            `${tx.refType || '-'} / ${tx.refId || '-'}`,
                            `${Number(tx.amount || 0).toLocaleString()}P`,
                            `${Number(tx.balanceAfter || 0).toLocaleString()}P`,
                            tx.memo || '-',
                            formatDateTimeMinute(tx.createdAt),
                        ])}
                        emptyText="포인트 거래가 없습니다."
                    />
                </div>
            )}

            {selectedAuditLog && (
                <BillingAuditLogDetailModal log={selectedAuditLog} onClose={() => setSelectedAuditLog(null)} />
            )}

            {grantTargets?.items?.length > 0 && (
                <div
                    className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
                    onClick={() => {
                        setGrantTargets(null)
                        setGrantReason('')
                    }}
                >
                    <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">구독일 부여</h3>
                        <p className="mt-1 text-xs text-slate-500">선택한 회원 전원에게 동일한 총 일수를 부여합니다. 프리셋은 복수 선택 시 합산됩니다.</p>
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">대상 ({grantTargets.items.length}명)</label>
                                <div className="max-h-32 overflow-y-auto rounded border bg-slate-50 px-3 py-2 text-xs text-slate-800">
                                    {grantTargets.items.map((it) => (
                                        <div key={it.id}>{it.targetName || `ID ${it.id}`}</div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="mb-2 block font-medium text-slate-700">일수 프리셋 (복수 선택·합산)</label>
                                <div className="flex flex-wrap gap-3">
                                    {GRANT_DAY_PRESET_OPTIONS.map((d) => (
                                        <label key={d} className="inline-flex cursor-pointer items-center gap-2 text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={grantDayPresetSelection.has(d)}
                                                onChange={() => toggleGrantDayPreset(d)}
                                            />
                                            <span>{d}일</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">추가 일수 (선택)</label>
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full rounded border px-3 py-2"
                                    placeholder="0"
                                    value={grantDaysExtra}
                                    onChange={(e) => setGrantDaysExtra(e.target.value)}
                                />
                            </div>
                            <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-slate-800">
                                합계 부여 일수: <span className="font-semibold text-brand-navy">{resolveGrantTotalDays()}</span>일
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">사유</label>
                                <textarea className="w-full rounded border px-3 py-2" rows={3} value={grantReason} onChange={(e) => setGrantReason(e.target.value)} placeholder="운영자 수동 부여" />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => { setGrantTargets(null); setGrantReason('') }}>취소</button>
                            <button type="button" className="rounded bg-brand-navy px-3 py-2 text-sm text-white" onClick={grantSubscriptionDays}>부여</button>
                        </div>
                    </div>
                </div>
            )}

            {selectedOrder && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
                    <div className="w-full max-w-xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">주문 상세</h3>
                                <p className="mt-1 text-sm text-slate-500">{selectedOrder.orderNo || selectedOrder.id}</p>
                            </div>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setSelectedOrder(null)}>닫기</button>
                        </div>
                        {orderDetailLoading ? (
                            <div className="mt-6 text-sm text-slate-500">상세 로딩 중...</div>
                        ) : (
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <InfoBlock label="구매자" value={selectedOrder.buyerName || '-'} />
                                <InfoBlock label="조직" value={selectedOrder.orgName || '-'} />
                                <InfoBlock label="플랜" value={selectedOrder.planName || selectedOrder.planCode || '-'} />
                                <InfoBlock label="상태" valueNode={<StatusBadge value={selectedOrder.status} type="order" />} />
                                <InfoBlock label="쿠폰" value={formatCouponReservation(selectedOrder)} />
                                <InfoBlock label="결제수단" value={selectedOrder.paymentMethod || '-'} />
                                <InfoBlock label="PG" value={selectedOrder.pgProvider || '-'} />
                                <InfoBlock label="PG 거래번호" value={selectedOrder.pgTransactionId || '-'} />
                                <InfoBlock label="PG 주문번호" value={selectedOrder.pgOrderId || '-'} />
                                <InfoBlock label="금액" value={currency(selectedOrder.amount)} />
                                <InfoBlock label="결제일시" value={formatDateTimeMinute(selectedOrder.paidAt || selectedOrder.createdAt)} />
                                {selectedOrder.payment && (
                                    <div className="md:col-span-2 rounded border p-4">
                                        <div className="text-slate-500">결제 원장</div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <InfoBlock compact label="Payment ID" value={selectedOrder.payment.id || '-'} />
                                            <InfoBlock compact label="상태" valueNode={<StatusBadge value={selectedOrder.payment.status} type="payment" />} />
                                            <InfoBlock compact label="금액" value={currency(selectedOrder.payment.amount)} />
                                            <InfoBlock compact label="PG" value={selectedOrder.payment.pgProvider || '-'} />
                                            <InfoBlock compact label="결제수단" value={selectedOrder.payment.pgMethod || '-'} />
                                            <InfoBlock compact label="결제시각" value={formatDateTimeMinute(selectedOrder.payment.paidAt)} />
                                        </div>
                                    </div>
                                )}
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">증빙 문서</div>
                                    {Array.isArray(selectedOrder.documents) && selectedOrder.documents.length > 0 ? (
                                        <div className="mt-3 overflow-x-auto">
                                            <table className="min-w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                                        <th className="px-3 py-2 text-left">문서번호</th>
                                                        <th className="px-3 py-2 text-left">종류</th>
                                                        <th className="px-3 py-2 text-left">상태</th>
                                                        <th className="px-3 py-2 text-left">링크</th>
                                                        <th className="px-3 py-2 text-left">발행일시</th>
                        </tr>
                    </thead>
                    <tbody>
                                                    {selectedOrder.documents.map((doc) => (
                                                        <tr key={doc.documentId || doc.id} className="border-t">
                                                            <td className="px-3 py-2">{doc.id || doc.documentId || '-'}</td>
                                                            <td className="px-3 py-2">{doc.documentType || '-'}</td>
                                                            <td className="px-3 py-2"><StatusBadge value={doc.status} type="document" /></td>
                                                            <td className="px-3 py-2">{renderExternalLink(doc.metadata?.receiptUrl, '보기')}</td>
                                                            <td className="px-3 py-2">{formatDateTimeMinute(doc.issuedAt || doc.requestedAt)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                                        </div>
                                    ) : (
                                        <div className="mt-2 text-sm text-slate-500">연결된 증빙이 없습니다.</div>
                                    )}
                    </div>
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">웹훅 이벤트</div>
                                    {Array.isArray(selectedOrder.webhookEvents) && selectedOrder.webhookEvents.length > 0 ? (
                                        <div className="mt-3 space-y-3">
                                            {selectedOrder.webhookEvents.map((event) => (
                                                <div key={event.id} className="rounded border bg-slate-50 p-3 text-xs">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="font-medium">{event.provider}</span>
                                                        <StatusBadge value={event.eventType} type="event" />
                                                        <span className="text-slate-500">{formatDateTimeMinute(event.receivedAt)}</span>
                </div>
                                                    <div className="mt-2 text-slate-600">{event.eventId}</div>
                                                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-slate-700">
                                                        {prettyJson(event.payload)}
                                                    </pre>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="mt-2 text-sm text-slate-500">수신된 웹훅 이벤트가 없습니다.</div>
                                    )}
                                </div>
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">영수증</div>
                                    <div className="mt-2">{renderExternalLink(selectedOrder.receiptUrl, selectedOrder.receiptUrl || '미발행')}</div>
                                </div>
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">메타데이터</div>
                                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700 whitespace-pre-wrap">
                                        {prettyJson(selectedOrder.metadata)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {selectedQuotaSubscription && (
                <div
                    className="fixed inset-0 bg-black/30 flex items-center justify-center p-4"
                    onClick={() => {
                        setSelectedQuotaSubscription(null)
                        setQuotaLimitDraft({})
                    }}
                >
                    <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">할당량 상세</h3>
                                <p className="mt-1 text-sm text-slate-500">{selectedQuotaSubscription.targetName} / {selectedQuotaSubscription.planName}</p>
                            </div>
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm"
                                onClick={() => {
                                    setSelectedQuotaSubscription(null)
                                    setQuotaLimitDraft({})
                                }}
                            >
                                닫기
                            </button>
                        </div>
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <InfoBlock label="구독 상태" valueNode={<StatusBadge value={selectedQuotaSubscription.status} type="subscription" />} />
                            <InfoBlock label="기간 시작" value={formatDate(selectedQuotaSubscription.currentQuotaPeriod?.periodStartAt)} />
                            <InfoBlock label="기간 종료" value={formatDate(selectedQuotaSubscription.currentQuotaPeriod?.periodEndAt)} />
                            <InfoBlock label="다음 리셋" value={formatDate(selectedQuotaSubscription.nextResetAt)} />
                        </div>
                        <div className="mt-6 rounded border p-4">
                            <div className="text-sm font-medium">기능별 Counter</div>
                            <div className="mt-3 max-h-[32vh] overflow-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                            <th className="px-3 py-2 text-left">기능</th>
                                            <th className="px-3 py-2 text-left">모드</th>
                                            <th className="px-3 py-2 text-left">한도</th>
                                            <th className="px-3 py-2 text-left">사용</th>
                                            <th className="px-3 py-2 text-left">잔여</th>
                                            <th className="px-3 py-2 text-left">액션</th>
                        </tr>
                    </thead>
                    <tbody>
                                        {(selectedQuotaSubscription.currentQuotaPeriod?.counters || []).map((counter) => (
                                            <tr key={counter.featureCode} className="border-t">
                                                <td className="px-3 py-2">{counter.featureName}</td>
                                                <td className="px-3 py-2"><StatusBadge value={counter.quotaMode} type="quota" /></td>
                                                <td className="px-3 py-2">{counter.quotaMode === 'UNLIMITED' ? '무제한' : counter.limitValue ?? 0}</td>
                                                <td className="px-3 py-2">{counter.usedValue ?? 0}</td>
                                                <td className="px-3 py-2">{counter.remainingValue === -1 ? '무제한' : counter.remainingValue ?? 0}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-col gap-2">
                                                        <button
                                                            type="button"
                                                            className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                            disabled={quotaConsumeLoading || quotaLimitAdjustLoading || counter.quotaMode === 'NONE'}
                                                            onClick={() => consumeSubscriptionQuota(selectedQuotaSubscription.id, counter.featureCode)}
                                                        >
                                                            1회 차감
                                                        </button>
                                                        {counter.quotaMode === 'LIMITED' && (
                                                            <div className="flex flex-wrap items-center gap-1">
                                                                <input
                                                                    type="number"
                                                                    className="w-[4.5rem] rounded border px-1 py-0.5 text-xs"
                                                                    placeholder="±한도"
                                                                    value={quotaLimitDraft[counter.featureCode] ?? ''}
                                                                    onChange={(e) => setQuotaLimitDraft((prev) => ({
                                                                        ...prev,
                                                                        [counter.featureCode]: e.target.value,
                                                                    }))}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                                                                    disabled={quotaConsumeLoading || quotaLimitAdjustLoading}
                                                                    onClick={() => adjustSubscriptionQuotaLimitRow(selectedQuotaSubscription.id, counter.featureCode)}
                                                                >
                                                                    한도 반영
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                            </div>
                        </div>
                        <div className="mt-4 rounded border p-4">
                            <div className="text-sm font-medium">최근 사용 이력</div>
                            <div className="mt-3 space-y-2 text-xs text-slate-600">
                                {(selectedQuotaSubscription.currentQuotaPeriod?.recentEvents || []).length === 0 && (
                                    <div className="text-slate-500">사용 이력이 없습니다.</div>
                                )}
                                {(selectedQuotaSubscription.currentQuotaPeriod?.recentEvents || []).slice(0, 10).map((event) => (
                                    <div key={event.id} className="rounded bg-slate-50 px-3 py-2">
                                        {event.featureName} / {event.units} / {event.refType} / {formatDateTimeMinute(event.createdAt)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedTrialHistory && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setSelectedTrialHistory(null)}>
                    <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Trial 이력</h3>
                                <p className="mt-1 text-sm text-slate-500">{selectedTrialHistory.subscription.targetName} / {selectedTrialHistory.subscription.planName}</p>
                            </div>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setSelectedTrialHistory(null)}>닫기</button>
                        </div>
                        {trialHistoryLoading ? (
                            <div className="mt-6 text-sm text-slate-500">Trial 이력 로딩 중...</div>
                        ) : (
                            <div className="mt-6 max-h-[55vh] overflow-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                            <th className="px-3 py-2 text-left">상태</th>
                                            <th className="px-3 py-2 text-left">유형</th>
                                            <th className="px-3 py-2 text-left">기간</th>
                                            <th className="px-3 py-2 text-left">소스</th>
                                            <th className="px-3 py-2 text-left">사유</th>
                                            <th className="px-3 py-2 text-left">생성시각</th>
                        </tr>
                    </thead>
                    <tbody>
                                        {selectedTrialHistory.rows.map((row) => (
                                            <tr key={row.id} className="border-t">
                                                <td className="px-3 py-2"><StatusBadge value={row.status} type="trial" /></td>
                                                <td className="px-3 py-2">{row.trialType || '-'}</td>
                                                <td className="px-3 py-2">{`${formatDate(row.startAt)} ~ ${formatDate(row.endAt)}`}</td>
                                                <td className="px-3 py-2">{row.grantSourceType || '-'}</td>
                                                <td className="px-3 py-2">{row.reason || '-'}</td>
                                                <td className="px-3 py-2">{formatDateTimeMinute(row.createdAt)}</td>
                            </tr>
                        ))}
                                        {selectedTrialHistory.rows.length === 0 && (
                                            <tr>
                                                <td className="px-3 py-8 text-center text-slate-500" colSpan={6}>Trial 이력이 없습니다.</td>
                                            </tr>
                                        )}
                    </tbody>
                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {bulkTrialModal && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setBulkTrialModal(null)}>
                    <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">{bulkTrialModal.revoke ? '일괄 Trial 회수' : '일괄 Trial 지급'}</h3>
                        <div className="mt-4 space-y-4 text-sm">
                <div>
                                <label className="mb-1 block text-slate-600">대상 건수</label>
                                <div className="rounded border bg-slate-50 px-3 py-2">{selectedSubscriptionIds.length}건</div>
                </div>
                            {!bulkTrialModal.revoke && (
                                <div>
                                    <label className="mb-1 block text-slate-600">부여 일수</label>
                                    <input type="number" className="w-full rounded border px-3 py-2" value={bulkTrialDays} onChange={(e) => setBulkTrialDays(e.target.value)} />
            </div>
                            )}
                            <div>
                                <label className="mb-1 block text-slate-600">사유</label>
                                <textarea className="w-full rounded border px-3 py-2" rows={3} value={bulkTrialReason} onChange={(e) => setBulkTrialReason(e.target.value)} />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setBulkTrialModal(null)}>취소</button>
                            <button className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={bulkTrialLoading} onClick={submitBulkTrial}>
                                {bulkTrialLoading ? '처리 중...' : '실행'}
                        </button>
            </div>
                    </div>
                </div>
            )}

            {bulkUpdateModal && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => {
                    setBulkUpdateModal(null)
                    setBulkUpdateReason('')
                }}>
                    <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">{bulkUpdateModal.title}</h3>
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <label className="mb-1 block text-slate-600">변경 대상</label>
                                <div className="rounded border bg-slate-50 px-3 py-2">
                                    {selectedSubscriptions.slice(0, 5).map((item) => item.targetName).join(', ')}
                                    {selectedSubscriptions.length > 5 ? ` 외 ${selectedSubscriptions.length - 5}명` : ''}
                        </div>
                        </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="mb-1 block text-slate-600">대상 건수</label>
                                    <div className="rounded border bg-slate-50 px-3 py-2">{selectedSubscriptionIds.length}건</div>
                        </div>
                                <div>
                                    <label className="mb-1 block text-slate-600">변경 값</label>
                                    <div className="rounded border bg-slate-50 px-3 py-2">{bulkUpdateModal.nextValueLabel}</div>
                    </div>
                    </div>
                            <div>
                                <label className="mb-1 block text-slate-600">변경 사유</label>
                                <textarea
                                    className="w-full rounded border px-3 py-2"
                                    rows={4}
                                    value={bulkUpdateReason}
                                    onChange={(e) => setBulkUpdateReason(e.target.value)}
                                    placeholder="운영 변경 사유를 입력해주세요."
                                />
                </div>
                    </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => {
                                setBulkUpdateModal(null)
                                setBulkUpdateReason('')
                            }}>
                                취소
                            </button>
                            <button className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={bulkActionLoading} onClick={submitBulkUpdate}>
                                {bulkActionLoading ? '변경 중...' : '변경 실행'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {bulkComposeOpen && (
                <div
                    className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
                    onClick={() => {
                        if (!bulkComposeLoading) setBulkComposeOpen(false)
                    }}
                >
                    <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">선택 회원 일괄 적용</h3>
                        <p className="mt-1 text-xs text-slate-500">구독 상태·플랜·구독일 부여·할당량 한도 조정을 한 번에 지정합니다. 비워 둔 항목은 변경하지 않습니다. billing이 준비된 선택 항목만 적용됩니다.</p>
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">구독 상태 (선택)</label>
                                <select className="w-full rounded border px-3 py-2" value={bulkComposeStatus} onChange={(e) => setBulkComposeStatus(e.target.value)}>
                                    <option value="">변경 안 함</option>
                                    <option value="TRIAL">{formatBillingStatusLabel('TRIAL', 'subscription')}</option>
                                    <option value="ACTIVE">{formatBillingStatusLabel('ACTIVE', 'subscription')}</option>
                                    <option value="SUSPENDED">{formatBillingStatusLabel('SUSPENDED', 'subscription')}</option>
                                    <option value="EXPIRED">{formatBillingStatusLabel('EXPIRED', 'subscription')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">플랜 (선택)</label>
                                <select className="w-full rounded border px-3 py-2" value={bulkComposePlan} onChange={(e) => setBulkComposePlan(e.target.value)}>
                                    <option value="">변경 안 함</option>
                                    {plans.map((plan) => (
                                        <option key={plan.planCode} value={plan.planCode}>{plan.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">구독일 부여 (일, 비우면 생략)</label>
                                <input
                                    type="number"
                                    min={1}
                                    className="w-full rounded border px-3 py-2"
                                    placeholder="예: 7"
                                    value={bulkComposeGrantDays}
                                    onChange={(e) => setBulkComposeGrantDays(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">할당량 한도 조정 (현재 과금 주 구독 행 기준)</label>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <select
                                        className="w-full rounded border px-3 py-2"
                                        value={bulkComposeQuotaFeature}
                                        onChange={(e) => setBulkComposeQuotaFeature(e.target.value)}
                                    >
                                        <option value="">기능 선택 안 함</option>
                                        <option value="CLAIM_FAX">CLAIM_FAX (팩스)</option>
                                        <option value="GROUP_SMS">GROUP_SMS (문자)</option>
                                        <option value="HEALTH_ANALYSIS">HEALTH_ANALYSIS (분석)</option>
                                    </select>
                                    <input
                                        type="number"
                                        className="w-full rounded border px-3 py-2"
                                        placeholder="한도 증감 (예: 10, -5)"
                                        value={bulkComposeQuotaDelta}
                                        onChange={(e) => setBulkComposeQuotaDelta(e.target.value)}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-slate-500">기능과 증감값을 함께 넣어야 적용됩니다. 사용·예약을 밑돌 수 없습니다.</p>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">사유</label>
                                <textarea
                                    className="w-full rounded border px-3 py-2"
                                    rows={3}
                                    value={bulkComposeReason}
                                    onChange={(e) => setBulkComposeReason(e.target.value)}
                                    placeholder="운영자 일괄 적용"
                                />
                            </div>
                            <div className="rounded border bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                대상: billing 준비된 회원 <span className="font-semibold text-slate-900">{selectedSubscriptions.filter((s) => s.billingReady !== false).length}</span>명
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" disabled={bulkComposeLoading} onClick={() => setBulkComposeOpen(false)}>취소</button>
                            <button type="button" className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={bulkComposeLoading} onClick={submitBulkCompose}>
                                {bulkComposeLoading ? '적용 중...' : '적용'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {billingBootstrapModal && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => {
                    setBillingBootstrapModal(null)
                    setBillingBootstrapConfirmText('')
                }}>
                    <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">누락 billing 생성</h3>
                        <div className="mt-4 space-y-4 text-sm">
                            <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                                운영환경에서도 실행 가능한 기능입니다. 기존 회원 중 `billing_account` 또는 기본 `subscription` 이 없는 사용자에게만 생성합니다.
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="mb-1 block text-slate-600">처리 상한</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20000"
                                        className="w-full rounded border px-3 py-2"
                                        value={billingBootstrapModal.limit}
                                        onChange={(e) => setBillingBootstrapModal((prev) => ({ ...prev, limit: Math.max(1, Math.min(20000, Number(e.target.value) || 5000)) }))}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-slate-600">실행 확인 문구</label>
                                    <input
                                        className="w-full rounded border px-3 py-2"
                                        value={billingBootstrapConfirmText}
                                        onChange={(e) => setBillingBootstrapConfirmText(e.target.value)}
                                        placeholder="BOOTSTRAP"
                                    />
                                </div>
                            </div>
                            <div className="text-xs text-slate-500">
                                권장 순서: 먼저 `누락 billing dry-run` 으로 누락 건수를 확인한 뒤 실제 생성을 실행하세요.
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => {
                                setBillingBootstrapModal(null)
                                setBillingBootstrapConfirmText('')
                            }}>
                                취소
                            </button>
                            <button className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={billingBootstrapLoading} onClick={submitBillingBootstrap}>
                                {billingBootstrapLoading ? '생성 중...' : '생성 실행'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {billingBootstrapResult && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setBillingBootstrapResult(null)}>
                    <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-3">
                    <div>
                                <h3 className="text-lg font-semibold">{billingBootstrapResult.mode === 'dryRun' ? '누락 billing dry-run 결과' : '누락 billing 생성 결과'}</h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    누락 대상 총 {billingBootstrapResult.payload?.totalMissingUsers || 0}명 중 이번 실행 {billingBootstrapResult.payload?.scannedMissingUsers || 0}명 처리
                                    {billingBootstrapResult.payload?.hasMore ? ` / 상한 ${billingBootstrapResult.payload?.limit || 0}건 적용` : ''}
                                </p>
                        </div>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setBillingBootstrapResult(null)}>닫기</button>
                    </div>
                        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                            <InfoBlock compact label="누락 계정" value={`${billingBootstrapResult.payload?.missingAccounts || 0}건`} />
                            <InfoBlock compact label="누락 구독" value={`${billingBootstrapResult.payload?.missingSubscriptions || 0}건`} />
                            <InfoBlock compact label="생성 계정" value={`${billingBootstrapResult.payload?.createdAccounts || 0}건`} />
                            <InfoBlock compact label="생성 구독" value={`${billingBootstrapResult.payload?.createdSubscriptions || 0}건`} />
                        </div>
                        {(billingBootstrapResult.payload?.failedUsers || 0) > 0 && (
                            <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                                실패 {billingBootstrapResult.payload?.failedUsers || 0}건이 있어 결과 목록에서 확인이 필요합니다.
                    </div>
                        )}
                        <div className="mt-5 max-h-[45vh] overflow-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-3 py-2 text-left">이름</th>
                                        <th className="px-3 py-2 text-left">이메일</th>
                                        <th className="px-3 py-2 text-left">누락 계정</th>
                                        <th className="px-3 py-2 text-left">누락 구독</th>
                                        <th className="px-3 py-2 text-left">결과</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(billingBootstrapResult.payload?.items || []).slice(0, 50).map((item) => (
                                        <tr key={`bootstrap-${item.userId}`} className="border-t">
                                            <td className="px-3 py-2">{item.userName || '-'}</td>
                                            <td className="px-3 py-2">{item.userEmail || '-'}</td>
                                            <td className="px-3 py-2">{item.missingAccount ? '예' : '아니오'}</td>
                                            <td className="px-3 py-2">{item.missingSubscription ? '예' : '아니오'}</td>
                                            <td className="px-3 py-2">{item.success === false ? (item.error || '실패') : '성공'}</td>
                                        </tr>
                                    ))}
                                    {(billingBootstrapResult.payload?.items || []).length === 0 && (
                                        <tr>
                                            <td className="px-3 py-8 text-center text-slate-500" colSpan={5}>누락된 회원이 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {(billingBootstrapResult.payload?.items || []).length > 50 && (
                            <div className="mt-3 text-xs text-slate-500">
                                결과는 상위 50건만 표시했습니다.
                </div>
            )}
                    </div>
                </div>
            )}
        </section>
    )
}

function DashboardCard({ label, value, hint = '', tone = 'slate' }) {
    const toneClasses = {
        blue: 'border-blue-200 bg-blue-50/70',
        violet: 'border-violet-200 bg-violet-50/70',
        emerald: 'border-emerald-200 bg-emerald-50/70',
        amber: 'border-amber-200 bg-amber-50/70',
        rose: 'border-rose-200 bg-rose-50/70',
        slate: 'border-slate-200 bg-slate-50/70',
    }
    return (
        <div className={`rounded-xl border p-4 ${toneClasses[tone] || toneClasses.slate}`}>
            <div className="text-sm font-medium text-slate-600">{label}</div>
            <div className="mt-2 text-2xl font-semibold">{value}</div>
            {hint && <div className="mt-2 text-xs text-slate-500">{hint}</div>}
                        </div>
    )
}

function DashboardSection({ title, tone = 'slate', children }) {
    const toneClasses = {
        blue: 'from-blue-50 to-white border-blue-200',
        violet: 'from-violet-50 to-white border-violet-200',
        emerald: 'from-emerald-50 to-white border-emerald-200',
        amber: 'from-amber-50 to-white border-amber-200',
        rose: 'from-rose-50 to-white border-rose-200',
        slate: 'from-slate-50 to-white border-slate-200',
    }
    return (
        <div className={`rounded-2xl border bg-gradient-to-b p-5 shadow-sm ${toneClasses[tone] || toneClasses.slate}`}>
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold">{title}</h2>
                            </div>
                        </div>
            <div className="mt-4">{children}</div>
        </div>
    )
}

function InfoBlock({ label, value, valueNode = null, compact = false }) {
    return (
        <div className={`rounded border ${compact ? 'p-3' : 'p-4'}`}>
            <div className="text-slate-500">{label}</div>
            <div className="mt-2 font-medium break-all">{valueNode || value}</div>
                        </div>
    )
}

function FormField({ label, children }) {
    return (
        <div>
            <label className="mb-1 block text-slate-600">{label}</label>
            {children}
                    </div>
    )
}

function AdminTable({ title, columns, rows, emptyText }) {
    return (
        <div className="mt-6 rounded border bg-white p-5">
            <h2 className="text-lg font-semibold">{title}</h2>
            <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            {columns.map((column) => <th key={column} className="px-4 py-3 text-left">{column}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={`${title}-${rowIndex}`} className="border-t">
                                {row.map((cell, cellIndex) => <td key={`${title}-${rowIndex}-${cellIndex}`} className="px-4 py-3">{cell}</td>)}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-4 py-8 text-center text-slate-500" colSpan={columns.length}>{emptyText}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                        </div>
                            </div>
    )
}

function TablePager({ totalCount, page, pageSize, totalPages, onPrev, onNext }) {
    if (totalCount <= 0) return null
    return (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
            <div>
                {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} / {totalCount}
                        </div>
            <div className="flex items-center gap-2">
                <button
                    className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
                    disabled={page <= 1}
                    onClick={onPrev}
                >
                    이전
                </button>
                <div className="min-w-[96px] text-center">
                    {page} / {totalPages} 페이지
                        </div>
                <button
                    className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
                    disabled={page >= totalPages}
                    onClick={onNext}
                >
                    다음
                </button>
                    </div>
                </div>
    )
}

function currency(value) {
    if (typeof value !== 'number') return '-'
    return `₩${value.toLocaleString()}`
}

function csvEscape(value) {
    const normalized = String(value ?? '')
    if (normalized.includes('"') || normalized.includes(',') || normalized.includes('\n')) {
        return `"${normalized.replace(/"/g, '""')}"`
    }
    return normalized
}

function downloadCsvFile(rows, filename) {
    const csv = '\uFEFF' + rows.map((row) => row.map(csvEscape).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
}

function sortButtonClass(currentKey, columnKey) {
    const isActive = currentKey === columnKey
    return `inline-flex items-center gap-1 rounded px-2 py-1 transition ${isActive ? 'bg-slate-200 font-semibold text-slate-900' : 'hover:bg-slate-100 hover:text-slate-900'}`
}

function sortMark(currentKey, currentDirection, columnKey) {
    if (currentKey !== columnKey) return '↕'
    return currentDirection === 'asc' ? '▲' : '▼'
}

function formatSubscriptionExpiry(value) {
    if (!value) return '만료일 없음'
    return `${formatDate(value) || '-'} ${formatExpiryRemaining(value)}`
}

function formatExpiryRemaining(value) {
    if (!value) return '만료일 없음'
    const diff = diffInDays(value)
    if (diff === null) return '-'
    if (diff > 0) return `D-${diff}`
    if (diff === 0) return 'D-Day'
    return `만료 ${Math.abs(diff)}일 경과`
}

function diffInDays(value) {
    if (!value) return null
    const target = new Date(value)
    if (Number.isNaN(target.getTime())) return null
    const today = new Date()
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate())
    return Math.round((startOfTarget.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24))
}

function renderExternalLink(url, label) {
    if (!url) return '-'
    return (
        <a className="text-xs text-brand-navy underline" href={url} target="_blank" rel="noreferrer">
            {label}
        </a>
    )
}

function prettyJson(value) {
    try {
        return JSON.stringify(value ?? {}, null, 2)
    } catch (e) {
        return String(value ?? '{}')
    }
}

function createInitialRewardForm() {
    return {
        rewardType: 'POINT_GRANT',
        targetRole: 'REDEEMER',
        value: '',
        expireDays: '',
    }
}

function createInitialPromotionForm() {
    return {
        promotionType: 'COUPON',
        triggerType: 'CODE_REDEEM',
        code: '',
        name: '',
        description: '',
        status: 'ACTIVE',
        perUserLimit: '',
        totalLimit: '',
        rewards: [createInitialRewardForm()],
    }
}

function formatRewardLabel(reward) {
    const type = reward?.rewardType || '-'
    const value = reward?.valueInt ?? reward?.value ?? 0
    const targetRole = reward?.targetRole || 'REDEEMER'
    const expireDays = reward?.valueJson?.expireDays
    const suffix = type.includes('POINT') ? 'P' : type.includes('DISCOUNT') ? (type === 'PERCENT_DISCOUNT' ? '%' : '원') : '일'
    const expireText = expireDays ? ` / 만료 ${expireDays}일` : ''
    return `${formatPromotionRewardTargetRoleLabel(targetRole)} · ${formatPromotionRewardTypeLabel(type)} ${value}${suffix}${expireText}`
}

function formatCouponReservation(order) {
    const couponCode = order?.pricingContext?.couponCode || order?.couponReservation?.code
    if (!couponCode) return '-'
    return `${couponCode} / ${formatCouponReservationStatus(order?.couponReservation?.status)}`
}

function formatCouponReservationStatus(status) {
    if (status === 'RESERVED') return '예약'
    if (status === 'USED') return '사용'
    if (status === 'CANCELLED') return '해제'
    if (status === 'EXPIRED') return '만료'
    return status || '-'
}

function StatusBadge({ value, type = 'default' }) {
    const text = formatBillingStatusLabel(value, type)
    const tone = billingStatusToneClass(value, type)
    return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${tone}`}>{text}</span>
}

function QuotaSummaryList({ item }) {
    const summary = Array.isArray(item?.quotaSummary) ? item.quotaSummary : []
    const visible = summary.filter((row) => row?.enabled !== false && row?.quotaMode !== 'NONE').slice(0, 4)
    if (visible.length === 0) {
        return (
            <>
                <div>팩스 잔여 {formatLimit(item.claimFaxRemaining)} / 예약 {formatLimit(item.claimFaxReserved)} / 사용 {formatLimit(item.claimFaxUsed)}</div>
                <div className="mt-1">문자 잔여 {formatLimit(item.groupSmsRemaining)} / 분석 잔여 {formatLimit(item.healthAnalysisRemaining)}</div>
            </>
        )
    }
    return (
        <div className="space-y-1">
            {visible.map((row) => (
                <div key={row.featureCode} className="flex min-w-[16rem] items-center justify-between gap-3">
                    <span className="max-w-[9rem] truncate text-slate-700">{row.featureName || row.featureCode}</span>
                    <span className="whitespace-nowrap text-slate-900">{formatQuotaUsage(row)}</span>
                </div>
            ))}
            {summary.length > visible.length && (
                <div className="text-slate-400">외 {summary.length - visible.length}개 기능</div>
            )}
        </div>
    )
}

function formatQuotaUsage(row) {
    if (row.quotaMode === 'UNLIMITED') {
        return `무제한 / 사용 ${row.usedValue ?? 0}`
    }
    return `잔여 ${formatLimit(row.remainingValue ?? 0)} / 예약 ${row.reservedValue ?? 0} / 사용 ${row.usedValue ?? 0}`
}

function formatLimit(value) {
    return value === -1 ? '무제한' : value
}
